import React, { useState, useEffect } from 'react';
import { Ticket, User, LogOut, Plus, Calendar, DollarSign, Users, ArrowLeft, ShoppingCart, CheckCircle, Trophy, Bell, Mail, Phone } from 'lucide-react';
import { db, auth, googleProvider } from './firebase';
import { collection, addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where, orderBy } from 'firebase/firestore';
import { signInWithPopup, signInWithCredential, signOut, onAuthStateChanged, GoogleAuthProvider } from 'firebase/auth';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Capacitor } from '@capacitor/core';

// Simulación de Router (en producción usarías React Router)
function Router({ children }) {
  return children;
}

export default function Acceso7Rifas() {
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'rifa-detail', 'datos-personales', 'payment', 'mis-rifas', 'admin-panel', 'ganadores'
  const [selectedRifa, setSelectedRifa] = useState(null);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [showSorteoModal, setShowSorteoModal] = useState(false);
  const [numeroGanador, setNumeroGanador] = useState(null);
  const [sorteoEnCurso, setSorteoEnCurso] = useState(false);
  const [numerosPendientes, setNumerosPendientes] = useState([]); // Números pendientes de confirmación
  const [showSeleccionarPremioModal, setShowSeleccionarPremioModal] = useState(false);
  const [selectedPremioForSorteo, setSelectedPremioForSorteo] = useState(null);
  
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showCreateRifa, setShowCreateRifa] = useState(false);
  const [rifas, setRifas] = useState([]);
  const [users, setUsers] = useState([]);
  const [compras, setCompras] = useState([]);
  const [ganadores, setGanadores] = useState([]); // [{rifaId, numeroGanador, ganadorNombre, ganadorEmail, fechaSorteo}]
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]); // Usuarios pendientes de aprobación
  const [showCompradorModal, setShowCompradorModal] = useState(false);
  const [compradorInfo, setCompradorInfo] = useState(null);
  const [showCondicionesModal, setShowCondicionesModal] = useState(false);
  const [aceptoCondiciones, setAceptoCondiciones] = useState(false);
  const [isCreatingRifa, setIsCreatingRifa] = useState(false);
  
  // Sistema de URLs
  const getCurrentPath = () => {
    return window.location.pathname;
  };

  const navigateTo = (path) => {
    window.history.pushState({}, '', path);
    handleRouteChange();
  };

  const handleRouteChange = () => {
    const path = getCurrentPath();
    
    // Redirigir / a /inicio
    if (path === '/') {
      navigateTo('/inicio');
      return;
    }
    
    if (path === '/inicio') {
      setCurrentPage('home');
    } else if (path.startsWith('/rifa/')) {
      const rifaId = path.split('/')[2];
      const rifa = rifas.find(r => r.id.toString() === rifaId);
      if (rifa) {
        setSelectedRifa(rifa);
        setCurrentPage('rifa-detail');
      } else {
        navigateTo('/inicio');
      }
    } else if (path === '/datos-personales') {
      setCurrentPage('datos-personales');
    } else if (path === '/confirmar-compra') {
        setCurrentPage('payment');
    } else if (path === '/mis-rifas') {
      setCurrentPage('mis-rifas');
    } else if (path === '/admin') {
      if (isAdmin()) {
        setCurrentPage('admin-panel');
      } else {
        alert('❌ No tienes permisos de administrador');
        navigateTo('/inicio');
      }
    } else if (path === '/todas-las-rifas') {
      if (isAdmin()) {
        setCurrentPage('todas-las-rifas');
        } else {
        alert('❌ No tienes permisos de administrador');
        navigateTo('/inicio');
        }
    } else if (path === '/gestionar-rifas') {
      if (user) {
        setCurrentPage('gestionar-rifas');
      } else {
        alert('❌ Debes iniciar sesión para gestionar rifas');
        navigateTo('/inicio');
      }
    } else if (path === '/ganadores') {
      setCurrentPage('ganadores');
    } else if (path === '/login') {
      setShowLogin(true);
    } else if (path === '/registro') {
      setShowRegister(true);
    } else if (path === '/crear-rifa') {
      setShowCreateRifa(true);
    } else if (path === '/habilitar-rifa') {
      // Verificar si el usuario ya aceptó las condiciones
      const condicionesAceptadas = localStorage.getItem('condiciones_habilitar_rifa');
      if (!condicionesAceptadas) {
        setShowCondicionesModal(true);
      }
      setCurrentPage('habilitar-rifa');
    } else {
      navigateTo('/inicio');
    }
  };

  // Escuchar cambios en la URL
  useEffect(() => {
    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [rifas]);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '' });
  const [showTransferenciaModal, setShowTransferenciaModal] = useState(false);
  const [showDatosModal, setShowDatosModal] = useState(false);
  const [rifaForm, setRifaForm] = useState({
    tipo: 'rifa', // 'rifa' o 'rapadita'
    titulo: '',
    descripcion: '',
    premios: ['', ''], // Array de premios (mínimo 1, por defecto 2)
    precio: '',
    totalNumeros: '',
    fechaSorteo: '',
    alias: '',
    cbu: '',
    whatsapp: ''
  });
  const [buyerForm, setBuyerForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: ''
  });

  // Funciones para manejar múltiples premios
  const addPremio = () => {
    setRifaForm({
      ...rifaForm,
      premios: [...rifaForm.premios, '']
    });
  };

  const removePremio = (index) => {
    const newPremios = rifaForm.premios.filter((_, i) => i !== index);
    if (newPremios.length > 0) {
      setRifaForm({
        ...rifaForm,
        premios: newPremios
      });
    }
  };

  const updatePremio = (index, value) => {
    const newPremios = [...rifaForm.premios];
    newPremios[index] = value;
    setRifaForm({
      ...rifaForm,
      premios: newPremios
    });
  };

  // Funciones para Firestore
  const saveUserToFirestore = async (userData) => {
    try {
      const docRef = await addDoc(collection(db, 'usuarios'), {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Usuario guardado en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      throw error;
    }
  };

  const saveRifaToFirestore = async (rifaData) => {
    try {
      const docRef = await addDoc(collection(db, 'rifas'), {
        ...rifaData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Rifa guardada en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar rifa:', error);
      throw error;
    }
  };

  const loadUsersFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'usuarios'));
      const usersData = [];
      querySnapshot.forEach((doc) => {
        const userData = { id: doc.id, ...doc.data() };
        console.log('📄 Usuario cargado desde Firestore:', userData);
        // Si no tiene uid, usar el id del documento como uid
        if (!userData.uid) {
          userData.uid = doc.id;
          console.log('⚠️ Usuario sin uid, usando doc.id:', doc.id);
        }
        usersData.push(userData);
      });
      return usersData;
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      return [];
    }
  };

  const loadRifasFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'rifas'));
      const rifasData = [];
      const seenIds = new Set(); // Para evitar duplicados
      
      querySnapshot.forEach((doc) => {
        if (!seenIds.has(doc.id)) {
          seenIds.add(doc.id);
          rifasData.push({ id: doc.id, ...doc.data() });
        }
      });
      
      console.log(`📦 Rifas cargadas: ${rifasData.length} únicas`);
      return rifasData;
    } catch (error) {
      console.error('Error al cargar rifas:', error);
      return [];
    }
  };

  const loadComprasFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'compras'));
      const comprasData = [];
      querySnapshot.forEach((doc) => {
        comprasData.push({ id: doc.id, ...doc.data() });
      });
      console.log(`🛒 Compras cargadas: ${comprasData.length}`);
      return comprasData;
    } catch (error) {
      console.error('Error al cargar compras:', error);
      return [];
    }
  };

  const saveCompraToFirestore = async (compraData) => {
    try {
      const docRef = await addDoc(collection(db, 'compras'), {
        ...compraData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('Compra guardada en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar compra:', error);
      throw error;
    }
  };

  const updateRifaInFirestore = async (rifaId, updateData) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      await updateDoc(rifaRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      console.log('Rifa actualizada en Firestore:', rifaId);
    } catch (error) {
      console.error('Error al actualizar rifa:', error);
      throw error;
    }
  };

  const saveGanadorToFirestore = async (ganadorData) => {
    try {
      const docRef = await addDoc(collection(db, 'ganadores'), {
        ...ganadorData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('🎉 Ganador guardado en Firestore con ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error al guardar ganador:', error);
      throw error;
    }
  };

  const loadGanadoresFromFirestore = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'ganadores'));
      const ganadoresData = [];
      querySnapshot.forEach((doc) => {
        ganadoresData.push({ id: doc.id, ...doc.data() });
      });
      console.log(`🏆 Ganadores cargados: ${ganadoresData.length}`);
      return ganadoresData;
    } catch (error) {
      console.error('Error al cargar ganadores:', error);
      return [];
    }
  };

  // Funciones para eliminar datos de Firestore
  const deleteRifaFromFirestore = async (rifaId) => {
    try {
      const rifaRef = doc(db, 'rifas', rifaId);
      await deleteDoc(rifaRef);
      console.log('🗑️ Rifa eliminada de Firestore:', rifaId);
    } catch (error) {
      console.error('Error al eliminar rifa:', error);
      throw error;
    }
  };

  const deleteGanadoresFromFirestore = async (rifaId) => {
    try {
      const querySnapshot = await getDocs(collection(db, 'ganadores'));
      const deletePromises = [];
      
      querySnapshot.forEach((doc) => {
        const ganador = doc.data();
        if (ganador.rifaId === rifaId) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      await Promise.all(deletePromises);
      console.log(`🗑️ Ganadores eliminados de Firestore para rifa: ${rifaId}`);
    } catch (error) {
      console.error('Error al eliminar ganadores:', error);
      throw error;
    }
  };

  const deleteComprasFromFirestore = async (rifaId) => {
    try {
      const querySnapshot = await getDocs(collection(db, 'compras'));
      const deletePromises = [];
      
      querySnapshot.forEach((doc) => {
        const compra = doc.data();
        if (compra.rifaId === rifaId) {
          deletePromises.push(deleteDoc(doc.ref));
        }
      });
      
      await Promise.all(deletePromises);
      console.log(`🗑️ Compras eliminadas de Firestore para rifa: ${rifaId}`);
    } catch (error) {
      console.error('Error al eliminar compras:', error);
      throw error;
    }
  };

  // Función para limpiar todos los datos de una rifa
  const limpiarDatosDeRifa = async (rifaId) => {
    try {
      console.log('🧹 Iniciando limpieza de datos para rifa:', rifaId);
      
      // Eliminar rifa
      await deleteRifaFromFirestore(rifaId);
      
      // Eliminar ganadores
      await deleteGanadoresFromFirestore(rifaId);
      
      // Eliminar compras
      await deleteComprasFromFirestore(rifaId);
      
      // Actualizar estado local
      setRifas(rifas.filter(r => r.id !== rifaId));
      setGanadores(ganadores.filter(g => g.rifaId !== rifaId));
      setCompras(compras.filter(c => c.rifaId !== rifaId));
      
      console.log('✅ Datos de rifa eliminados exitosamente:', rifaId);
    } catch (error) {
      console.error('Error al limpiar datos de rifa:', error);
    }
  };

  const handleConfirmarPago = async (rifaId, numerosPendientes) => {
    try {
      console.log('=== CONFIRMANDO PAGO ===');
      console.log('Rifa ID:', rifaId);
      console.log('Números a confirmar:', numerosPendientes);
      
      // Actualizar rifa: mover números de pendientes a reservados
      const rifa = rifas.find(r => r.id === rifaId);
      if (!rifa) {
        alert('Rifa no encontrada');
        return;
      }
      
      // Encontrar las compras pendientes que corresponden a estos números
      const comprasPendientes = compras.filter(c => 
        c.rifaId === rifaId && 
        c.estado === 'pendiente' &&
        numerosPendientes.some(num => c.numeros.includes(num))
      );
      
      console.log('Compras pendientes encontradas:', comprasPendientes);
      
      // Actualizar compras a estado 'confirmado'
      const comprasActualizadas = compras.map(c => {
        if (comprasPendientes.some(cp => cp.id === c.id)) {
          return { ...c, estado: 'confirmado' };
        }
        return c;
      });
      setCompras(comprasActualizadas);
      
      // Actualizar compras en Firestore
      for (const compra of comprasPendientes) {
        try {
          const compraRef = doc(db, 'compras', compra.id);
          await updateDoc(compraRef, {
            estado: 'confirmado',
            fechaConfirmacion: new Date().toISOString()
          });
          console.log('Compra actualizada en Firestore:', compra.id);
        } catch (error) {
          console.error('Error al actualizar compra:', error);
        }
      }
      
      const updatedRifas = rifas.map(r => {
        if (r.id === rifaId) {
        return {
            ...r,
            numerosReservados: [...r.numerosReservados, ...numerosPendientes],
            numerosPendientes: r.numerosPendientes.filter(n => !numerosPendientes.includes(n)),
            numerosVendidos: r.numerosVendidos + numerosPendientes.length
          };
        }
        return r;
      });
      
      // Actualizar en Firestore
      await updateRifaInFirestore(rifaId, {
        numerosReservados: [...rifa.numerosReservados, ...numerosPendientes],
        numerosPendientes: rifa.numerosPendientes.filter(n => !numerosPendientes.includes(n)),
        numerosVendidos: rifa.numerosVendidos + numerosPendientes.length
      });
      
      setRifas(updatedRifas);
      
      alert(`✅ Pago confirmado exitosamente!\n\nNúmeros confirmados: ${numerosPendientes.join(', ')}\nTotal: $${numerosPendientes.length * parseFloat(rifa.precio)}`);
      
    } catch (error) {
      console.error('Error al confirmar pago:', error);
      alert('Error al confirmar el pago. Por favor intenta de nuevo.');
    }
  };



  const handleEliminarRifa = async (rifaId) => {
    try {
      const rifa = rifas.find(r => r.id === rifaId);
      if (!rifa) {
        alert('Rifa no encontrada');
        return;
      }

      // SOLO EL ADMIN PUEDE ELIMINAR RIFAS
      if (!isAdmin()) {
        alert('❌ SOLO EL ADMINISTRADOR PUEDE ELIMINAR RIFAS\n\nContacta al administrador si necesitas eliminar una rifa.');
        return;
      }

      // Verificar si hay números vendidos o pendientes
      const tieneVentas = rifa.numerosVendidos > 0 || (rifa.numerosPendientes?.length || 0) > 0;
      
      if (tieneVentas) {
        const confirmacion = confirm(
          `⚠️ ADVERTENCIA: Esta rifa tiene ${rifa.numerosVendidos} números vendidos y ${rifa.numerosPendientes?.length || 0} números pendientes.\n\n` +
          `¿Estás seguro de que quieres eliminar la rifa?\n\n` +
          `Esta acción no se puede deshacer.`
        );
        
        if (!confirmacion) {
          return;
        }
      } else {
        const confirmacion = confirm(
          `¿Estás seguro de que quieres eliminar la rifa "${rifa.titulo}"?\n\n` +
          `Esta acción no se puede deshacer.`
        );
        
        if (!confirmacion) {
          return;
        }
      }

      // Eliminar de Firestore usando la función de limpieza completa
      await limpiarDatosDeRifa(rifaId);
      
      // Si estamos viendo la rifa eliminada, volver a mis rifas
      if (selectedRifa?.id === rifaId) {
        navigateTo('/mis-rifas');
      }
      
      alert('✅ Rifa y todos sus datos (ganadores, compradores) eliminados exitosamente');
      
    } catch (error) {
      console.error('Error al eliminar rifa:', error);
      alert('Error al eliminar la rifa. Por favor intenta de nuevo.');
    }
  };

  // Funciones para el sistema de administración
  const isAdmin = () => {
    const admin = user?.uid === 'Trz2xMBhVjc9pnOOGkTPegeNGGD2';
    console.log('🔐 isAdmin check:', { 
      userEmail: user?.email, 
      userUid: user?.uid, 
      isAdmin: admin 
    });
    return admin;
  };

  const canCreateRifa = () => {
    if (!user) return false;
    if (isAdmin()) return true; // Solo el admin puede crear rifas ilimitadas
    
    // Verificar si el usuario tiene rifas permitidas (solo usuarios aprobados)
    // Buscar por ID, UID o email para mayor compatibilidad
    const userData = users.find(u => 
      u.id === user.id || 
      u.uid === user.uid || 
      u.email === user.email
    );
    
    console.log('🔍 canCreateRifa check:', {
      user: user,
      userData: userData,
      hasUserData: !!userData,
      pagoRealizado: userData?.pagoRealizado,
      rifasPermitidas: userData?.rifasPermitidas
    });
    
    // Si no existe el usuario en la base de datos (usuario nuevo), NO puede crear rifas
    if (!userData) {
      console.log('❌ Usuario no encontrado en la base de datos - NO puede crear rifas');
      return false;
    }
    
    // El usuario debe estar aprobado (pagoRealizado = true)
    if (!userData.pagoRealizado) {
      console.log('❌ Usuario NO aprobado - NO puede crear rifas');
      return false;
    }
    
    // El usuario debe tener rifas permitidas > 0
    if (!userData.rifasPermitidas || userData.rifasPermitidas <= 0) {
      console.log('❌ Usuario no tiene rifas permitidas - NO puede crear rifas');
      return false;
    }
    
    // Contar cuántas rifas ha creado el usuario
    const rifasCreadas = rifas.filter(r => r.creadorId === user.id).length;
    
    // Verificar si ha alcanzado el límite
    const puedeCrear = rifasCreadas < userData.rifasPermitidas;
    
    console.log('📊 Validación detallada:', {
      rifasCreadas,
      rifasPermitidas: userData.rifasPermitidas,
      comparacion: `${rifasCreadas} < ${userData.rifasPermitidas} = ${puedeCrear}`,
      puedeCrear,
      mensaje: puedeCrear ? '✅ Puede crear rifas' : `❌ Ya alcanzó el límite de ${userData.rifasPermitidas} rifas`
    });
    
    return puedeCrear;
  };

  const handleApproveUser = async (userUid, rifasPermitidas) => {
    try {
      console.log('🔍 handleApproveUser called with:', { userUid, rifasPermitidas });
      
      if (!userUid) {
        alert('❌ Error: UID del usuario no válido');
        return;
      }
      
      // El userUid es el ID del documento en Firestore
      const userRef = doc(db, 'usuarios', userUid);
      
      // Verificar que el documento existe
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        alert('❌ No se encontró el usuario en Firestore');
        return;
      }
      
      await updateDoc(userRef, {
        rifasPermitidas: rifasPermitidas,
        pagoRealizado: true,
        solicitudPendiente: false, // Marcar solicitud como procesada
        fechaPago: new Date().toISOString(),
        ultimoAcceso: new Date().toISOString()
      });

      // Actualizar estado local
      const updatedUsers = users.map(u => {
        if (u.id === userUid) {
          return {
            ...u,
            rifasPermitidas: rifasPermitidas,
            pagoRealizado: true,
            solicitudPendiente: false,
            fechaPago: new Date().toISOString()
          };
        }
        return u;
      });
      setUsers(updatedUsers);

      alert(`✅ Usuario aprobado exitosamente\n\nRifas permitidas: ${rifasPermitidas}`);
    } catch (error) {
      console.error('Error al aprobar usuario:', error);
      alert('Error al aprobar usuario. Por favor intenta de nuevo.');
    }
  };

  const handleRejectUser = async (userUid) => {
    try {
      if (confirm('¿Estás seguro de que quieres rechazar este usuario?')) {
        // El userUid es el ID del documento en Firestore
        const userRef = doc(db, 'usuarios', userUid);
        
        // Verificar que el documento existe
        const userDoc = await getDoc(userRef);
        if (!userDoc.exists()) {
          alert('❌ No se encontró el usuario en Firestore');
          return;
        }
        
        await deleteDoc(userRef);

        // Actualizar estado local
        const updatedUsers = users.filter(u => u.id !== userUid);
        setUsers(updatedUsers);

        alert('✅ Usuario rechazado');
      }
    } catch (error) {
      console.error('Error al rechazar usuario:', error);
      alert('Error al rechazar usuario. Por favor intenta de nuevo.');
    }
  };

  // Cargar datos desde Firestore y verificar sesión persistente
  useEffect(() => {
    const loadData = async () => {
      // Listener para cambios en el estado de autenticación de Firebase
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          console.log('Usuario autenticado con Firebase:', firebaseUser);
          
          // Crear objeto de usuario para la aplicación
          const appUser = {
            id: firebaseUser.uid,
            uid: firebaseUser.uid, // Agregar uid explícitamente para isAdmin()
            name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
            email: firebaseUser.email,
            avatar: firebaseUser.photoURL,
            provider: 'google'
          };
          
          setUser(appUser);
          localStorage.setItem('acceso7_user', JSON.stringify(appUser));
      } else {
          console.log('Usuario no autenticado');
          setUser(null);
          localStorage.removeItem('acceso7_user');
        }
      });

      // Verificar si hay una sesión guardada (fallback)
      const savedUser = localStorage.getItem('acceso7_user');
      if (savedUser && !user) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error al cargar usuario guardado:', error);
          localStorage.removeItem('acceso7_user');
        }
      }

      try {
        // Cargar usuarios desde Firestore
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);
        console.log('Usuarios cargados desde Firestore:', usersData);
        
        // Cargar rifas desde Firestore
        const rifasData = await loadRifasFromFirestore();
        setRifas(rifasData);
        console.log('Rifas cargadas desde Firestore:', rifasData);
        
        // Cargar compras desde Firestore
        const comprasData = await loadComprasFromFirestore();
        setCompras(comprasData);
        console.log('Compras cargadas desde Firestore:', comprasData);
        
        // Cargar ganadores desde Firestore
        const ganadoresData = await loadGanadoresFromFirestore();
        setGanadores(ganadoresData);
        console.log('Ganadores cargados desde Firestore:', ganadoresData);
        
        console.log('Datos cargados desde Firestore:', { users: usersData.length, rifas: rifasData.length, compras: comprasData.length, ganadores: ganadoresData.length });
      } catch (error) {
        console.error('Error al cargar datos desde Firestore:', error);
        
        // Si Firestore falla, mantener arrays vacíos
        console.log('Firestore no disponible, usando arrays vacíos');
        setUsers([]);
        setRifas([]);
        setCompras([]);
      }
    };

    loadData();
  }, []);

  // useEffect para limpiar datos de rifas finalizadas después de 48 horas
  useEffect(() => {
    const checkAndCleanExpiredRifas = async () => {
      const ahora = new Date();
      
      rifas.forEach(rifa => {
        // Solo procesar rifas finalizadas
        if (rifa.finalizada && rifa.ganadorId) {
          // Buscar el ganador para obtener la fecha de sorteo
          const ganador = ganadores.find(g => g.rifaId === rifa.id);
          
          if (ganador && ganador.fechaSorteo) {
            const fechaSorteo = new Date(ganador.fechaSorteo);
            const horasTranscurridas = (ahora - fechaSorteo) / (1000 * 60 * 60);
            
            console.log(`🕐 Rifa ${rifa.titulo}: ${horasTranscurridas.toFixed(2)} horas transcurridas desde finalización`);
            
            // Si han pasado más de 48 horas, limpiar los datos
            if (horasTranscurridas >= 48) {
              console.log(`🗑️ Limpiando datos de rifa finalizada: ${rifa.titulo}`);
              limpiarDatosDeRifa(rifa.id);
            }
          }
        }
      });
    };

    // Verificar cada minuto
    const interval = setInterval(checkAndCleanExpiredRifas, 60000);
    
    // Ejecutar inmediatamente
    checkAndCleanExpiredRifas();
    
    return () => clearInterval(interval);
  }, [rifas, ganadores]);

  const handleRegister = async () => {
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      alert('Por favor completa todos los campos');
      return;
    }
    
    try {
      // Verificar si el email ya existe
      const existingUser = users.find(u => u.email === registerForm.email);
      if (existingUser) {
        alert('Este email ya está registrado. Por favor usa otro email o inicia sesión.');
      return;
    }

      const newUser = {
        name: registerForm.name,
        email: registerForm.email,
        password: registerForm.password
      };

      // Guardar en Firestore
      const userId = await saveUserToFirestore(newUser);
      const userWithId = { id: userId, ...newUser };

      // Actualizar estado local
      setUsers([...users, userWithId]);
      
      // Auto-login después del registro
      setUser(userWithId);
      localStorage.setItem('acceso7_user', JSON.stringify(userWithId));
      
      setRegisterForm({ name: '', email: '', password: '' });
      setShowRegister(false);
      alert(`¡Registro exitoso! Bienvenido ${newUser.name}`);
    } catch (error) {
      console.error('Error en el registro:', error);
      alert('Error al registrar usuario. Por favor intenta de nuevo.');
    }
  };

  const handleLogin = () => {
    const foundUser = users.find(u => u.email === loginForm.email && u.password === loginForm.password);
    if (foundUser) {
      setUser(foundUser);
      // Guardar sesión en localStorage
      localStorage.setItem('acceso7_user', JSON.stringify(foundUser));
      setLoginForm({ email: '', password: '' });
      setShowLogin(false);
      alert(`¡Bienvenido ${foundUser.name}!`);
    } else {
      alert('Credenciales incorrectas');
    }
  };

  const handleLogout = async () => {
    try {
      // Cerrar sesión en Firebase Auth
      await signOut(auth);
      
      setUser(null);
      localStorage.removeItem('acceso7_user');
      setCurrentPage('home');
      alert('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Fallback: limpiar estado local
      setUser(null);
      localStorage.removeItem('acceso7_user');
      setCurrentPage('home');
      alert('Sesión cerrada correctamente');
    }
  };

  const handleGoogleLogin = async () => {
    try {
      console.log('🔵 Iniciando login con Google...');
      
      // Detectar si estamos en la app nativa de Capacitor
      const isNative = Capacitor.isNativePlatform();
      const platform = Capacitor.getPlatform();
      
      let result;
      
      if (isNative && platform === 'android') {
        // En Android, usar el plugin nativo de Google
        console.log('✅ Dispositivo Android NATIVO detectado, usando Google Auth nativo...');
        
        try {
          // Verificar si GoogleAuth está disponible
          if (!GoogleAuth) {
            console.error('❌ GoogleAuth plugin not available');
            alert('⚠️ Error: Google Auth no está disponible.\n\nPor favor, actualiza la app desde Play Store o contacta al soporte.');
            return;
          }
          
          console.log('🔄 Inicializando Google Auth...');
          // IMPORTANTE: Necesitamos el Web Client ID aquí, NO el Android Client ID
          // El error dice: "You must use a Web client as the server client ID"
          await GoogleAuth.initialize({
            clientId: '743099229873-8e7b8va87adt8sn80vhjjhqovbltvd5b.apps.googleusercontent.com',
            scopes: ['profile', 'email'],
            grantOfflineAccess: true,
          });
          
          console.log('✅ Google Auth initialized successfully');
          
          const googleUser = await GoogleAuth.signIn();
          console.log('✅ Google Auth successful:', googleUser);
          
          if (!googleUser || !googleUser.authentication || !googleUser.authentication.idToken) {
            console.error('❌ Invalid Google Auth response:', googleUser);
            alert('⚠️ Error: No se pudo obtener el token de Google.\n\nPor favor, intenta de nuevo.');
            return;
          }
          
          const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
          result = await signInWithCredential(auth, credential);
        } catch (googleError) {
          console.error('❌ Error con Google Auth nativo:', googleError);
          alert(`⚠️ Error al iniciar sesión con Google.\n\nError: ${googleError.message}\n\nCódigo: ${googleError.code || 'N/A'}`);
          return;
        }
      } else {
        // En escritorio o web, usar popup
        result = await signInWithPopup(auth, googleProvider);
      }
      
      const user = result.user;
      console.log('✅ Usuario autenticado con Google:', user.email);
      
      const appUser = {
        id: user.uid,
        uid: user.uid,
        name: user.displayName || user.email.split('@')[0],
        email: user.email,
        avatar: user.photoURL,
        provider: 'google'
      };
      
      const existingUser = users.find(u => u.email === user.email);
      
      if (!existingUser) {
        const newUserData = {
          uid: user.uid,
          nombre: user.displayName || user.email.split('@')[0],
          username: user.displayName || user.email.split('@')[0],
          email: user.email,
          provider: 'google.com',
          rol: 'Participante',
          rifasPermitidas: 0,
          pagoRealizado: false,
          fechaRegistro: new Date().toISOString(),
          ultimoAcceso: new Date().toISOString(),
          telefono: '',
          tipo: 'email'
        };
        
        const userId = await saveUserToFirestore(newUserData);
        const userWithId = { id: userId, ...newUserData };
        appUser.id = userId;
        setUsers([...users, userWithId]);
      } else {
        appUser.id = existingUser.id;
      }
      
      setUser(appUser);
      localStorage.setItem('acceso7_user', JSON.stringify(appUser));
      setShowLogin(false);
      alert(`¡Bienvenido ${appUser.name}!`);
      
    } catch (error) {
      console.error('❌ Error en login con Google:', error);
      alert(`Error al iniciar sesión con Google.\n\n${error.message}`);
    }
  };


  const handleCreateRifa = async () => {
    // Prevenir duplicados: si ya se está creando una rifa, no hacer nada
    if (isCreatingRifa) {
      console.log('⏳ Ya se está creando una rifa, ignorando click duplicado');
      return;
    }
    
    // Verificar que el usuario esté logueado para crear rifas
    if (!user) {
      alert('Debes tener una cuenta para crear rifas');
      setShowLogin(true);
      return;
    }
    
    // Verificar permisos para crear rifas
    if (!canCreateRifa()) {
      if (isAdmin()) {
        alert('❌ Error: No se pudo verificar permisos de administrador');
        return;
      } else {
        const userData = users.find(u => u.id === user.id);
        const rifasCreadas = rifas.filter(r => r.creadorId === user.id).length;
        const rifasPermitidas = userData?.rifasPermitidas || 0;
        
        if (userData && userData.pagoRealizado && rifasCreadas >= rifasPermitidas) {
          alert(`❌ Has alcanzado el límite de rifas permitidas\n\nRifas creadas: ${rifasCreadas}\nRifas permitidas: ${rifasPermitidas}\n\nContacta al administrador si necesitas crear más rifas.`);
        } else {
          alert('❌ No tienes permisos para crear rifas\n\nDebes ser aprobado por el administrador para crear rifas.\n\nContacta al administrador para más información.');
        }
        return;
      }
    }
    
    // Validar que al menos haya un premio
    const premiosValidos = rifaForm.premios.filter(p => p.trim() !== '');
    if (premiosValidos.length === 0) {
      alert('Por favor agrega al menos un premio');
      return;
    }
    
    if (!rifaForm.titulo || !rifaForm.descripcion || !rifaForm.precio || !rifaForm.totalNumeros || !rifaForm.fechaSorteo) {
      alert('Por favor completa todos los campos');
      return;
    }

    if (!rifaForm.alias && !rifaForm.cbu) {
      alert('Debes ingresar tu Alias o CBU para recibir transferencias');
      return;
    }

    // Marcar que se está creando una rifa
    setIsCreatingRifa(true);

    try {
      console.log('=== CREANDO RIFA ===');
      console.log('Usuario actual:', user);
      console.log('Datos del formulario:', rifaForm);
      
      const newRifa = {
      ...rifaForm,
        premios: premiosValidos, // Solo guardar premios válidos
        premio: premiosValidos.join(', '), // Mantener compatibilidad con rifa.premio
        creadorId: user.id,
        creadorNombre: user.name,
        numerosVendidos: 0,
        numerosReservados: [],
        numerosPendientes: [],
        fechaCreacion: new Date().toISOString()
      };

      console.log('Nueva rifa a crear:', newRifa);

      // Guardar en Firestore
      const rifaId = await saveRifaToFirestore(newRifa);
      console.log('Rifa guardada en Firestore con ID:', rifaId);

      // Recargar rifas desde Firestore para evitar duplicados
      const rifasData = await loadRifasFromFirestore();
      setRifas(rifasData);
      
      console.log('Rifas recargadas desde Firestore. Total:', rifasData.length);
      
      setRifaForm({ tipo: 'rifa', titulo: '', descripcion: '', premios: ['', ''], precio: '', totalNumeros: '', fechaSorteo: '', alias: '', cbu: '', whatsapp: '' });
      setShowCreateRifa(false);
      setIsCreatingRifa(false); // Limpiar estado
      alert('¡Rifa creada exitosamente!');
    } catch (error) {
      console.error('Error al crear rifa:', error);
      setIsCreatingRifa(false); // Limpiar estado en caso de error
      alert('Error al crear la rifa. Por favor intenta de nuevo.');
    }
  };

  const handleVerRifa = (rifa) => {
    setSelectedRifa(rifa);
    setSelectedNumbers([]);
    navigateTo(`/rifa/${rifa.id}`);
  };

  const handleToggleNumber = (numero) => {
    const rifa = rifas.find(r => r.id === selectedRifa.id);
    
    // Si el número está vendido, mostrar información del comprador
    if (rifa.numerosReservados.includes(numero)) {
      console.log('🔴 Número vendido clicado:', numero);
      console.log('🔍 Buscando comprador...');
      console.log('Rifa ID:', rifa.id);
      console.log('Compras disponibles:', compras.length);
      console.log('Compras:', compras);
      
      const comprador = compras.find(c => 
        c.rifaId === rifa.id && 
        c.numeros.includes(numero) &&
        (c.estado === 'confirmado' || !c.estado) // Tolerar compras sin estado (legacy)
      );
      
      console.log('Comprador encontrado:', comprador);
      
      if (comprador) {
        setCompradorInfo({
          numero,
          nombre: comprador.compradorNombre,
          direccion: comprador.compradorDireccion,
          email: comprador.compradorEmail,
          telefono: comprador.compradorTelefono
        });
        setShowCompradorModal(true);
        console.log('✅ Modal abierto');
      } else {
        console.log('❌ No se encontró comprador para este número');
      }
      return;
    }

    if ((rifa.numerosPendientes || []).includes(numero)) {
      return; // Ya está pendiente
    }
    
    if (selectedNumbers.includes(numero)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== numero));
      } else {
      setSelectedNumbers([...selectedNumbers, numero]);
    }
  };

  const handleProceedToPayment = () => {
    if (selectedNumbers.length === 0) {
      alert('Debes seleccionar al menos un número');
      return;
    }
    // Limpiar formulario del comprador
    setBuyerForm({ nombre: '', apellido: '', email: '', telefono: '', direccion: '' });
    setShowDatosModal(true);
  };

  const handleProceedToConfirmation = () => {
    if (!buyerForm.nombre || !buyerForm.apellido || !buyerForm.email || !buyerForm.telefono || !buyerForm.direccion) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    // Advertencia: Verificar datos antes de continuar
    const confirmacion = confirm(
      '⚠️ IMPORTANTE: Verifica que tus datos sean correctos\n\n' +
      `Nombre: ${buyerForm.nombre} ${buyerForm.apellido}\n` +
      `Email: ${buyerForm.email}\n` +
      `Teléfono: ${buyerForm.telefono}\n` +
      `Dirección: ${buyerForm.direccion}\n\n` +
      '¿Confirmas que estos datos son correctos?'
    );
    
    if (!confirmacion) {
      return;
    }
    
    setShowDatosModal(false);
    navigateTo('/confirmar-compra');
  };

  const handleConfirmPayment = async () => {
    try {
      console.log('=== CONFIRMANDO PAGO ===');
      console.log('Números seleccionados:', selectedNumbers);
      console.log('Datos del comprador:', buyerForm);

    const updatedRifas = rifas.map(r => {
      if (r.id === selectedRifa.id) {
          // Solo transferencia - los números van a pendientes
        return {
          ...r,
            numerosPendientes: [...(r.numerosPendientes || []), ...selectedNumbers]
        };
      }
      return r;
    });
    
    const nuevaCompra = {
      rifaId: selectedRifa.id,
      rifaTitulo: selectedRifa.titulo,
        compradorNombre: `${buyerForm.nombre} ${buyerForm.apellido}`,
      compradorEmail: buyerForm.email,
      compradorTelefono: buyerForm.telefono,
        compradorDireccion: buyerForm.direccion,
      numeros: [...selectedNumbers],
      total: selectedNumbers.length * parseFloat(selectedRifa.precio),
        fecha: new Date().toISOString(),
        estado: 'pendiente'
      };
      
      // Guardar compra en Firestore
      const compraId = await saveCompraToFirestore(nuevaCompra);
      const compraWithId = { id: compraId, ...nuevaCompra };
      
      // Actualizar rifa en Firestore
      await updateRifaInFirestore(selectedRifa.id, {
        numerosPendientes: [...(selectedRifa.numerosPendientes || []), ...selectedNumbers]
      });
      
      setCompras([...compras, compraWithId]);
      setRifas(updatedRifas);
      
      // Simular envío de email
      enviarEmailConfirmacion(compraWithId);
      
      alert(`¡Reserva realizada exitosamente!\n\n✅ Confirmación enviada a: ${buyerForm.email}\n🎫 Tus números: ${selectedNumbers.sort((a, b) => a - b).join(', ')}\n💰 Total: $${nuevaCompra.total}\n\n⏳ Los números están en estado "Pendiente" hasta que el creador confirme tu transferencia.\n\n¡Mucha suerte!`);
    setSelectedNumbers([]);
      setBuyerForm({ nombre: '', apellido: '', email: '', telefono: '', direccion: '' });
      navigateTo('/inicio');
      
    } catch (error) {
      console.error('Error al confirmar pago:', error);
      alert('Error al procesar el pago. Por favor intenta de nuevo.');
    }
  };

  const enviarEmailConfirmacion = (compra) => {
    // Simulación de envío de email
    console.log('📧 EMAIL DE CONFIRMACIÓN ENVIADO:');
    console.log(`Para: ${compra.compradorEmail}`);
    console.log(`Asunto: Confirmación de compra - ${compra.rifaTitulo}`);
    console.log(`Números: ${compra.numeros.join(', ')}`);
    console.log(`Total: ${compra.total}`);
    console.log('---');
  };

  const realizarSorteo = async (premioIndex) => {
    if (selectedRifa.numerosReservados.length === 0) {
      alert('No hay números vendidos para realizar el sorteo');
      return;
    }

    // Obtener premios
    const premios = selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : []);
    const premioSeleccionado = premios[premioIndex];
    
    if (!premioSeleccionado) {
      alert('No hay más premios para sortear');
      return;
    }

    // Obtener números que ya ganaron (excluir del sorteo)
    const numerosGanadores = ganadores
      .filter(g => g.rifaId === selectedRifa.id)
      .map(g => g.numeroGanador);
    
    // Números disponibles para sortear (excluyendo ganadores)
    const numerosDisponibles = selectedRifa.numerosReservados.filter(num => !numerosGanadores.includes(num));
    
    if (numerosDisponibles.length === 0) {
      alert('No hay números disponibles para sortear (todos ya ganaron)');
      return;
    }

    setShowSorteoModal(true);
    setSorteoEnCurso(true);
    setNumeroGanador(null);

    // Animación de sorteo
    let contador = 0;
    const maxIteraciones = 20;
    
    const intervalo = setInterval(() => {
      const numeroAleatorio = numerosDisponibles[
        Math.floor(Math.random() * numerosDisponibles.length)
      ];
      setNumeroGanador(numeroAleatorio);
      contador++;

      if (contador >= maxIteraciones) {
        clearInterval(intervalo);
        setSorteoEnCurso(false);
        
        // Guardar ganador
        const compraGanadora = compras.find(c => 
          c.rifaId === selectedRifa.id && c.numeros.includes(numeroAleatorio)
        );

        const ahora = new Date();
        const fechaExpiracion = new Date(ahora.getTime() + (48 * 60 * 60 * 1000)); // 48 horas desde ahora
        
        const nuevoGanador = {
          id: Date.now(),
          rifaId: selectedRifa.id,
          rifaTitulo: selectedRifa.titulo,
          rifaPremio: premioSeleccionado,
          numeroGanador: numeroAleatorio,
          ganadorNombre: compraGanadora?.compradorNombre || 'Desconocido',
          ganadorEmail: compraGanadora?.compradorEmail || '',
          ganadorTelefono: compraGanadora?.compradorTelefono || '',
          fechaSorteo: ahora.toISOString(),
          fechaExpiracion: fechaExpiracion.toISOString(), // Expira en 48 horas
          creadorId: selectedRifa.creadorId
        };

        // Guardar ganador en Firestore (no await en callback)
        saveGanadorToFirestore(nuevoGanador).then(ganadorId => {
          nuevoGanador.firestoreId = ganadorId;
        }).catch(error => {
          console.error('Error al guardar ganador en Firestore:', error);
        });

        setGanadores([...ganadores, nuevoGanador]);

        // Verificar si todos los premios han sido sorteados
        const premiosSorteados = ganadores.filter(g => g.rifaId === selectedRifa.id).length + 1;
        const todosLosPremiosSorteados = premiosSorteados >= premios.length;

        // Marcar rifa como finalizada solo si todos los premios fueron sorteados
        if (todosLosPremiosSorteados) {
          const rifasActualizadas = rifas.map(r => {
            if (r.id === selectedRifa.id) {
              return { ...r, finalizada: true, ganadorId: nuevoGanador.id };
            }
            return r;
          });
          setRifas(rifasActualizadas);

          // Actualizar en Firestore
          updateRifaInFirestore(selectedRifa.id, {
            finalizada: true,
            ganadorId: nuevoGanador.id
          }).catch(error => {
            console.error('Error al actualizar rifa en Firestore:', error);
          });
        }

        // Notificar al ganador
        enviarNotificacionGanador(nuevoGanador);
      }
    }, 150);
  };

  const enviarNotificacionGanador = (ganador) => {
    console.log('🎉 NOTIFICACIÓN AL GANADOR:');
    console.log(`Para: ${ganador.ganadorEmail}`);
    console.log(`¡FELICIDADES ${ganador.ganadorNombre}!`);
    console.log(`Has ganado: ${ganador.rifaPremio}`);
    console.log(`Número ganador: ${ganador.numeroGanador}`);
    console.log('---');
  };


  // Header Component
  const Header = () => (
    <header className="bg-black bg-opacity-40 backdrop-blur-md border-b border-purple-500">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigateTo('/inicio')}
          >
            <Ticket className="w-8 h-8 md:w-10 md:h-10 text-yellow-400" />
            <h1 className="text-2xl md:text-4xl font-bold text-white tracking-wider">ACCESO7</h1>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {!user ? (
              <>
                <button
                  onClick={() => navigateTo('/login')}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition"
                >
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => navigateTo('/registro')}
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded-lg font-semibold transition"
                >
                  Registro
                </button>
              </>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            if (!user) {
                              alert('Debes crear una cuenta para crear rifas');
                              navigateTo('/registro');
                            } else {
                              navigateTo('/crear-rifa');
                            }
                          }}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                        >
                          <Plus className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="hidden md:inline">Crear Rifa</span>
                        </button>
                        {!isAdmin() && (
                        <button
                            onClick={() => navigateTo('/habilitar-rifa')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                        >
                            <User className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden md:inline">Habilitar Rifa</span>
                        </button>
                        )}
                        <button
                          onClick={() => navigateTo('/mis-rifas')}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm md:text-base font-semibold transition"
                        >
                          <span className="hidden md:inline">Mis Rifas</span>
                          <span className="md:hidden">Rifas</span>
                        </button>
                        {isAdmin() && (
                          <>
                          <button
                            onClick={() => navigateTo('/todas-las-rifas')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                          >
                            <Ticket className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden lg:inline">Todas las Rifas</span>
                          </button>
                          <button
                            onClick={() => navigateTo('/admin')}
                            className="px-3 py-1.5 md:px-6 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm md:text-base font-semibold flex items-center gap-1 md:gap-2 transition"
                          >
                            <User className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="hidden md:inline">Admin</span>
                          </button>
                          </>
                        )}
                        <button
                          onClick={() => navigateTo('/ganadores')}
                          className="px-3 py-1.5 md:px-6 md:py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm md:text-base font-semibold transition"
                        >
                          <span className="hidden md:inline">Ganadores</span>
                          <span className="md:hidden">🏆</span>
                        </button>
                        <div className="hidden md:flex items-center gap-2 text-white">
                          <User className="w-5 h-5" />
                  <span className="font-semibold">{user.name}</span>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                        >
                          <LogOut className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                      </div>
                    )}
          </div>
        </div>
      </div>
    </header>
  );

  // Home Page - Lista de Rifas
  const HomePage = () => {
    const [showFinalizadas, setShowFinalizadas] = useState(false);
    
    const rifasActivas = rifas.filter(r => !r.finalizada);
    const rifasFinalizadas = rifas.filter(r => r.finalizada);
    
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Todas las Rifas y Rapaditas Disponibles</h2>
            
            {/* Botón para ver rifas finalizadas - Solo visible para usuarios logueados */}
            {user && (
              <div className="flex justify-center mb-6">
              <button
                  onClick={() => setShowFinalizadas(!showFinalizadas)}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
              >
                  {showFinalizadas ? 'Ver Rifas Activas' : 'Ver Rifas Finalizadas'}
                  <span className="bg-gray-500 text-white px-2 py-1 rounded-full text-xs">
                    {rifasFinalizadas.length}
                  </span>
              </button>
            </div>
            )}
            
            {showFinalizadas ? (
              // Mostrar rifas finalizadas
              rifasFinalizadas.length === 0 ? (
              <div className="text-center py-16">
                  <Ticket className="w-24 h-24 text-gray-400 mx-auto mb-4 opacity-50" />
                  <p className="text-white text-xl">No hay rifas finalizadas aún</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rifasFinalizadas.map(rifa => {
            const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
            return (
                      <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-6 border border-gray-500 opacity-75">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-400 flex-1">{rifa.titulo}</h3>
                          <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                            ✅ Finalizada
                          </span>
                        </div>
                        <p className="text-gray-300 mb-4">{rifa.descripcion}</p>
                
                <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-gray-300">
                            <Ticket className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Premio:</span> {rifa.premio}
                  </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Precio:</span> ${rifa.precio}
                  </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Users className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                  </div>
                </div>

                        <div className="border-t border-gray-500 pt-3 mb-4">
                          <p className="text-gray-400 text-sm">Creado por: <span className="font-semibold text-gray-300">{rifa.creadorNombre}</span></p>
                </div>
                
                  <button 
                    onClick={() => handleVerRifa(rifa)}
                          className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition"
                        >
                          Ver Números y Ganadores
                  </button>
              </div>
            );
          })}
        </div>
              )
            ) : (
              // Mostrar rifas activas Y finalizadas mezcladas
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Rifas activas */}
                {rifasActivas.map(rifa => {
                  const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
                  return (
                    <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-6 border border-purple-400 hover:border-yellow-400 transition">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-yellow-400 flex-1">{rifa.titulo}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          rifa.tipo === 'rapadita' 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-purple-500 text-white'
                        }`}>
                          {rifa.tipo === 'rapadita' ? '⚡ Rapadita' : '🎫 Rifa'}
                        </span>
                      </div>
                      <p className="text-white mb-4">{rifa.descripcion}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-white">
                          <Ticket className="w-4 h-4 text-yellow-400" />
                          <span className="font-semibold">Premio:</span> {rifa.premio}
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <DollarSign className="w-4 h-4 text-green-400" />
                          <span className="font-semibold">Precio:</span> ${rifa.precio}
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <Users className="w-4 h-4 text-blue-400" />
                          <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                        </div>
                        <div className="flex items-center gap-2 text-white">
                          <Calendar className="w-4 h-4 text-purple-400" />
                          <span className="font-semibold">Sorteo:</span> {new Date(rifa.fechaSorteo).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Barra de progreso */}
                      <div className="mb-4">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-yellow-500 h-2 rounded-full transition-all"
                            style={{ width: `${porcentajeVendido}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-purple-300 mt-1">{porcentajeVendido.toFixed(1)}% vendido</p>
                      </div>
                      
                      <div className="border-t border-purple-400 pt-3 mb-4">
                        <p className="text-purple-300 text-sm">Creado por: <span className="font-semibold text-white">{rifa.creadorNombre}</span></p>
                      </div>
                      
                      {rifa.whatsapp && (
                        <a
                          href={`https://wa.me/${rifa.whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full mb-3 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg transition flex items-center justify-center gap-2"
                        >
                          <Phone className="w-5 h-5" />
                          Contactar por WhatsApp
                        </a>
                      )}
                      
                      <button 
                        onClick={() => handleVerRifa(rifa)}
                        className="w-full py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-lg transition"
                      >
                        Ver Números Disponibles
                      </button>
                    </div>
                  );
                })}
                
                {/* Rifas finalizadas (intercaladas) */}
                {rifasFinalizadas.map(rifa => {
                  const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
                  return (
                    <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-6 border border-gray-500 opacity-75">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-gray-400 flex-1">{rifa.titulo}</h3>
                        <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                          ✅ Finalizada
                        </span>
                      </div>
                      <p className="text-gray-300 mb-4">{rifa.descripcion}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-gray-300">
                          <Ticket className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold">Premio:</span> {rifa.premio}
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <DollarSign className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold">Precio:</span> ${rifa.precio}
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold">Números:</span> {rifa.numerosVendidos}/{rifa.totalNumeros}
                        </div>
                      </div>
                      
                      <div className="border-t border-gray-500 pt-3 mb-4">
                        <p className="text-gray-400 text-sm">Creado por: <span className="font-semibold text-gray-300">{rifa.creadorNombre}</span></p>
                      </div>
                      
                      <button 
                        onClick={() => handleVerRifa(rifa)}
                        className="w-full py-3 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition"
                      >
                        Ver Números y Ganadores
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {!showFinalizadas && rifasActivas.length === 0 && rifasFinalizadas.length === 0 && (
              <div className="text-center py-16">
                <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
                <p className="text-white text-xl">No hay rifas disponibles aún</p>
                <p className="text-purple-300 mt-2">¡Sé el primero en crear una!</p>
              </div>
            )}
            
            {/* Footer con enlace a política de privacidad */}
            <div className="mt-16 pt-8 border-t border-purple-500 text-center">
              <div className="flex flex-col items-center gap-4">
                <a
                  href="https://acceso7-32ad3.web.app/politica-privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-semibold transition flex items-center gap-2"
                >
                  🔒 Política de Privacidad
                </a>
                <p className="text-purple-300 text-sm">
                  © 2025 Acceso7 Rifas - Todos los derechos reservados
                </p>
              </div>
            </div>
                  
      </main>
    );
  };

  // Rifa Detail Page - Selección de números
  const RifaDetailPage = () => {
    const totalNumeros = parseInt(selectedRifa.totalNumeros);
    const numeros = Array.from({ length: totalNumeros }, (_, i) => i + 1);
    
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a las rifas
        </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400 mb-8">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">{selectedRifa.titulo}</h2>
          <p className="text-white text-lg mb-6">{selectedRifa.descripcion}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm mb-2">Premio</p>
              <div className="text-white font-bold space-y-1">
                {(selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                  <div key={index} className="text-sm">{index + 1}° {premio}</div>
                ))}
              </div>
            </div>
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm">Precio</p>
              <p className="text-white font-bold">${selectedRifa.precio}</p>
            </div>
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm">Disponibles</p>
              <p className="text-white font-bold">{totalNumeros - selectedRifa.numerosVendidos}</p>
            </div>
            <div className="bg-black bg-opacity-30 rounded-lg p-4">
              <p className="text-purple-300 text-sm">Sorteo</p>
              <p className="text-white font-bold">{new Date(selectedRifa.fechaSorteo).toLocaleDateString()}</p>
            </div>
            </div>
          </div>

        {/* Sección de Gestión de Pagos Pendientes (solo para el creador) */}
        {user && selectedRifa.creadorId === user.id && (selectedRifa.numerosPendientes || []).length > 0 && (
          <div className="bg-orange-900 bg-opacity-50 rounded-xl p-6 border border-orange-400 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-orange-400">⏳</span>
              Gestionar Pagos Pendientes
            </h3>
            <p className="text-orange-200 mb-4">
              Tienes <span className="font-bold text-yellow-400">{(selectedRifa.numerosPendientes || []).length}</span> números pendientes de confirmación
            </p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {(selectedRifa.numerosPendientes || []).map(num => (
                <span key={num} className="bg-orange-500 text-white px-3 py-1 rounded-lg font-bold">
                  {num}
                </span>
              ))}
                </div>
            
            <div className="text-orange-200 mb-4">
              <p className="text-sm">Total pendiente: <span className="font-bold text-yellow-400">${(selectedRifa.numerosPendientes?.length || 0) * parseFloat(selectedRifa.precio)}</span></p>
                </div>
            
            <button
              onClick={() => {
                if (confirm(`¿Confirmar TODOS los pagos pendientes?\n\nNúmeros: ${(selectedRifa.numerosPendientes || []).join(', ')}\nTotal: $${(selectedRifa.numerosPendientes?.length || 0) * parseFloat(selectedRifa.precio)}`)) {
                  handleConfirmarPago(selectedRifa.id, selectedRifa.numerosPendientes || []);
                }
              }}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Confirmar Todos los Pagos
            </button>
                </div>
        )}

        {/* Mostrar ganadores si la rifa está finalizada - MOVER ESTO ARRIBA */}
        {selectedRifa.finalizada && (() => {
          const ahora = new Date();
          const ganadoresValidos = ganadores.filter(g => {
            if (g.rifaId !== selectedRifa.id) return false;
            // Filtrar ganadores que no han expirado (menos de 48 horas)
            if (g.fechaExpiracion) {
              const fechaExp = new Date(g.fechaExpiracion);
              return fechaExp > ahora;
            }
            // Sin fecha de expiración = siempre válido
            return true;
          });
          
          return ganadoresValidos.length > 0 ? (
            <div className="bg-yellow-900 bg-opacity-50 rounded-xl p-6 border border-yellow-400 mb-8">
              <h4 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                <span>👑</span>
                Ganadores
              </h4>
              <div className="space-y-3">
                {ganadoresValidos.map(ganador => {
                // Buscar la compra para obtener más datos
                const compraGanadora = compras.find(c => 
                  c.rifaId === ganador.rifaId && 
                  c.numeros.includes(ganador.numeroGanador)
                );
                
                return (
                  <div key={ganador.id} className="bg-yellow-800 bg-opacity-30 rounded-lg p-4 border border-yellow-500">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">👑</span>
                        <p className="text-white font-bold text-xl">
                          Número Ganador: <span className="text-yellow-400">{ganador.numeroGanador}</span>
                        </p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 bg-black bg-opacity-30 rounded-lg p-3">
                <div>
                          <p className="text-yellow-200 text-sm mb-1">Ganador/a</p>
                          <p className="text-white font-bold text-lg">{ganador.ganadorNombre}</p>
                </div>
                <div>
                          <p className="text-yellow-200 text-sm mb-1">Premio</p>
                          <p className="text-white font-bold text-lg">{ganador.rifaPremio}</p>
                </div>
                        {compraGanadora && (
                          <>
                            {compraGanadora.compradorDireccion && (
                              <div className="md:col-span-2">
                                <p className="text-yellow-200 text-sm mb-1">Dirección</p>
                                <p className="text-white font-semibold">{compraGanadora.compradorDireccion}</p>
            </div>
                            )}
                          </>
          )}
        </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          ) : null;
        })()}

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400">
          <h3 className="text-2xl font-bold text-white mb-6">
            {selectedRifa.finalizada ? 'Números y Ganadores' : 'Selecciona tus números'}
          </h3>
          
            <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span className="text-white text-sm">Disponible</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded"></div>
                      <span className="text-white text-sm">Vendido</span>
                    </div>
                    <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-white text-sm">Seleccionado</span>
                      </div>
                    <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-white text-sm">Pendiente</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">👑</span>
                      <span className="text-white text-sm">Ganador</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2 mb-8">
                    {numeros.map(numero => {
              const isVendido = selectedRifa.numerosReservados.includes(numero);
              const isPendiente = (selectedRifa.numerosPendientes || []).includes(numero);
                      const isSelected = selectedNumbers.includes(numero);
                      
                      // Verificar si este número es ganador
                      const numeroGanador = ganadores.find(g => g.rifaId === selectedRifa.id && g.numeroGanador === numero);
                      const isGanador = !!numeroGanador;
                      
                      return (
                        <button
                          key={numero}
                          onClick={() => handleToggleNumber(numero)}
                  disabled={isPendiente || isGanador}
                          className={`
                    aspect-square rounded-lg font-bold text-sm transition-all relative overflow-hidden
                    ${isGanador
                      ? 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-white cursor-pointer hover:scale-105 border-2 border-yellow-300'
                      : isVendido 
                        ? 'bg-red-500 cursor-pointer opacity-70 hover:opacity-100 hover:scale-105' 
                        : isPendiente
                          ? 'bg-orange-500 cursor-not-allowed opacity-70'
                          : isSelected 
                            ? 'bg-yellow-500 text-black scale-110 shadow-lg' 
                            : 'bg-green-500 hover:bg-green-600 text-white hover:scale-105'
                    }
                  `}
                >
                          {isGanador && (
                            <span className="absolute top-0 left-0 right-0 text-center text-lg leading-none z-10">👑</span>
                          )}
                          <span className={isGanador ? 'mt-4 block' : ''}>{numero}</span>
                        </button>
                      );
                    })}
                  </div>

          {!selectedRifa.finalizada && selectedNumbers.length > 0 && (
            <div className="bg-black bg-opacity-40 rounded-lg p-6 mb-6">
              <h4 className="text-white font-bold mb-3">Números seleccionados ({selectedNumbers.length}):</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedNumbers.sort((a, b) => a - b).map(num => (
                  <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-full font-bold">
                    {num}
                  </span>
                ))}
              </div>
              <div className="flex justify-between items-center">
                <div className="text-white">
                  <p className="text-sm text-purple-300">Total a pagar:</p>
                  <p className="text-3xl font-bold text-yellow-400">
                    ${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}
                  </p>
                </div>
                <button
                  onClick={handleProceedToPayment}
                  className="px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg flex items-center gap-2 transition"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Proceder al Pago
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    );
  };

  // Datos Personales Page
  const DatosPersonalesPage = () => {
    return (
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => setCurrentPage('rifa-detail')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a la selección
        </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400">
          <h2 className="text-3xl font-bold text-yellow-400 mb-8 text-center">Tus Datos Personales</h2>
          
          <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-white mb-4">Resumen de tu Compra</h3>
            <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
                <p className="text-purple-300 text-sm mt-2">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                <p className="text-purple-300 text-sm">Números seleccionados</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedNumbers.sort((a, b) => a - b).map(num => (
                    <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-lg font-bold">
                      {num}
                    </span>
                  ))}
                </div>
                <p className="text-purple-300 text-sm">Total a pagar</p>
                <p className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</p>
                  </div>
            </div>
          </div>

          <div className="bg-black bg-opacity-30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-6">Información de Contacto</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white mb-2 font-semibold">Nombre *</label>
                <input
                  type="text"
                  value={buyerForm.nombre}
                  onChange={(e) => setBuyerForm({...buyerForm, nombre: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Juan"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Apellido *</label>
                <input
                  type="text"
                  value={buyerForm.apellido}
                  onChange={(e) => setBuyerForm({...buyerForm, apellido: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Pérez"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Correo Electrónico *</label>
                <input
                  type="email"
                  value={buyerForm.email}
                  onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="juan@ejemplo.com"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2 font-semibold">Número de Teléfono *</label>
                <input
                  type="tel"
                  value={buyerForm.telefono}
                  onChange={(e) => setBuyerForm({...buyerForm, telefono: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="+54 9 11 1234-5678"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-white mb-2 font-semibold">Dirección *</label>
                <input
                  type="text"
                  value={buyerForm.direccion}
                  onChange={(e) => setBuyerForm({...buyerForm, direccion: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                  placeholder="Av. Corrientes 1234, CABA"
                  required
                />
            </div>
          </div>
            <p className="text-purple-300 text-sm mt-4">* Campos obligatorios</p>
                  </div>

            <button
            onClick={handleProceedToConfirmation}
              className="w-full mt-8 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-xl transition"
            >
              <CheckCircle className="w-6 h-6" />
            Continuar a Confirmación
            </button>
        </div>
      </main>
    );
  };

  // Payment Page
  const PaymentPage = () => {
    const total = selectedNumbers.length * parseFloat(selectedRifa.precio);
    
    // Debug: Verificar datos de la rifa
    console.log('=== DEBUG PAYMENT PAGE ===');
    console.log('Rifa seleccionada:', selectedRifa);
    console.log('Alias:', selectedRifa.alias);
    console.log('CBU:', selectedRifa.cbu);
    console.log('Creador:', selectedRifa.creadorNombre);
    console.log('========================');
    
    return (
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigateTo(`/rifa/${selectedRifa.id}`)}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a la selección
        </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400">
          <h2 className="text-3xl font-bold text-yellow-400 mb-8 text-center">Confirmar Compra</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Detalles de la Rifa</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6 space-y-3">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                  <p className="text-white font-bold">{selectedRifa.titulo}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Precio por número</p>
                  <p className="text-white font-bold">${selectedRifa.precio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Sorteo</p>
                  <p className="text-white font-bold">{new Date(selectedRifa.fechaSorteo).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-white mb-4">Tus Números</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6">
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedNumbers.sort((a, b) => a - b).map(num => (
                    <span key={num} className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold text-lg">
                      {num}
                    </span>
                  ))}
                </div>
                <div className="border-t border-purple-400 pt-4">
                  <div className="flex justify-between text-white mb-2">
                    <span>Cantidad de números:</span>
                    <span className="font-bold">{selectedNumbers.length}</span>
                  </div>
                  <div className="flex justify-between text-white mb-4">
                    <span>Precio unitario:</span>
                    <span className="font-bold">${selectedRifa.precio}</span>
                  </div>
                  <div className="flex justify-between items-center bg-yellow-500 bg-opacity-20 rounded-lg p-4">
                    <span className="text-yellow-400 font-bold text-lg">Total a pagar:</span>
                    <span className="text-yellow-400 font-bold text-3xl">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

            <div>
            <h3 className="text-xl font-bold text-white mb-4">Datos del Comprador</h3>
              <div className="bg-black bg-opacity-30 rounded-lg p-6 space-y-3">
              <div>
                  <p className="text-purple-300 text-sm">Nombre Completo</p>
                  <p className="text-white font-bold">{buyerForm.nombre} {buyerForm.apellido}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm">Email</p>
                  <p className="text-white font-bold">{buyerForm.email}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm">Teléfono</p>
                  <p className="text-white font-bold">{buyerForm.telefono}</p>
          </div>
                <div>
                  <p className="text-purple-300 text-sm">Dirección</p>
                  <p className="text-white font-bold">{buyerForm.direccion}</p>
                </div>
              </div>
            </div>
          </div>


          <div className="bg-black bg-opacity-30 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              💳 Datos para Transferencia
            </h3>
            <div className="bg-purple-900 bg-opacity-50 rounded-lg p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🏦</span>
              </div>
                <div>
                  <h4 className="text-white font-bold text-lg">Transferencia Bancaria</h4>
                  <p className="text-purple-300 text-sm">Realiza la transferencia a los siguientes datos:</p>
            </div>
          </div>

              <div className="bg-green-900 bg-opacity-50 rounded-lg p-4 mb-4">
                <h5 className="text-green-400 font-bold mb-3">📋 Datos del Creador de la Rifa</h5>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-green-200">Creador:</span>
                    <span className="text-white font-bold">{selectedRifa.creadorNombre}</span>
                  </div>
                  
                  {/* Alias */}
                  {selectedRifa.alias && (
                    <div className="bg-green-800 bg-opacity-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-200 font-semibold">Alias:</span>
                        <span className="text-white font-bold text-xl bg-green-700 px-3 py-1 rounded">{selectedRifa.alias}</span>
                      </div>
                </div>
              )}
                  
                  {/* CBU */}
                  {selectedRifa.cbu && (
                    <div className="bg-green-800 bg-opacity-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-200 font-semibold">CBU:</span>
                        <span className="text-white font-bold text-lg bg-green-700 px-3 py-1 rounded">{selectedRifa.cbu}</span>
            </div>
          </div>
                  )}
                  
                  {/* Verificar que al menos uno esté disponible */}
                  {!selectedRifa.alias && !selectedRifa.cbu && (
                    <div className="bg-red-900 bg-opacity-50 rounded-lg p-3">
                      <p className="text-red-200 text-center font-semibold">
                        ⚠️ El creador no ha proporcionado datos de transferencia
                      </p>
            </div>
                  )}
                  
                  <div className="flex justify-between items-center bg-yellow-900 bg-opacity-50 rounded-lg p-3">
                    <span className="text-yellow-200 font-semibold">Monto a transferir:</span>
                    <span className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</span>
          </div>
                </div>
              </div>

              <div className="bg-yellow-900 bg-opacity-50 rounded-lg p-4">
                <p className="text-yellow-200 text-sm font-semibold">
                  ⚠️ Importante: Los números quedarán en estado "Pendiente" hasta que el creador confirme el pago.
                </p>
            </div>
          </div>
          </div>

                      <button 
              onClick={handleConfirmPayment}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 text-xl transition"
            >
              <CheckCircle className="w-6 h-6" />
              Reservar con Transferencia
                      </button>
        </div>
      </main>
    );
  };

  // Mis Rifas Page - Ver rifas creadas por el usuario
  const MisRifasPage = () => {
    const [showFinalizadas, setShowFinalizadas] = useState(false);
    // Filtrar rifas por creadorId - comparar tanto con user.id como con user.uid
    const misRifas = rifas.filter(r => 
      r.creadorId === user?.id || 
      r.creadorId === user?.uid || 
      (r.creadorNombre && user?.name && r.creadorNombre.toLowerCase() === user.name.toLowerCase())
    );
    const misRifasActivas = misRifas.filter(r => !r.finalizada);
    const misRifasFinalizadas = misRifas.filter(r => r.finalizada);
    
    // Debug: Mostrar información del usuario
    console.log('=== DEBUG MIS RIFAS ===');
    console.log('Usuario actual:', user);
    console.log('Usuario ID:', user?.id);
    console.log('Usuario nombre:', user?.name);
    console.log('Total rifas disponibles:', rifas.length);
    console.log('Rifas disponibles:', rifas);
    console.log('Rifas con creadorId:', rifas.map(r => ({ 
      id: r.id, 
      titulo: r.titulo, 
      creadorId: r.creadorId, 
      creadorNombre: r.creadorNombre,
      numerosPendientes: r.numerosPendientes,
      numerosVendidos: r.numerosVendidos,
      numerosReservados: r.numerosReservados
    })));
    console.log('Mis rifas filtradas:', misRifas);
    console.log('Filtro aplicado: creadorId === user?.id');
    console.log('Comparación:', rifas.map(r => ({ rifaId: r.id, rifaCreadorId: r.creadorId, userId: user?.id, coincide: r.creadorId === user?.id })));
    
    // Debug específico para números pendientes
    misRifas.forEach(rifa => {
      console.log(`Rifa ${rifa.titulo}:`, {
        numerosPendientes: rifa.numerosPendientes,
        numerosVendidos: rifa.numerosVendidos,
        numerosReservados: rifa.numerosReservados,
        totalNumeros: rifa.totalNumeros
      });
    });
    
    console.log('========================');
    
    if (!user) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-blur-md rounded-xl border border-purple-400">
            <h2 className="text-3xl font-bold text-white mb-4">🔐 Acceso Requerido</h2>
            <p className="text-purple-300 mb-6">Necesitas iniciar sesión para ver tus rifas</p>
                      <button 
              onClick={() => navigateTo('/login')}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold transition"
            >
              Iniciar Sesión
                      </button>
          </div>
        </main>
      );
    }

    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
                      <button 
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al inicio
                      </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {showFinalizadas ? 'Rifas Finalizadas' : 'Mis Rifas Creadas'}
              </h2>
              <p className="text-purple-300 text-lg">
                Hola <span className="text-yellow-400 font-bold">{user?.name || 'Usuario'}</span>, {showFinalizadas ? 'aquí puedes ver tus rifas finalizadas' : 'aquí puedes gestionar todas tus rifas'}
              </p>
            </div>
            <div className="flex gap-3">
            <button
                onClick={() => setShowFinalizadas(!showFinalizadas)}
                className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${
                  showFinalizadas
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              >
                {showFinalizadas ? '← Volver a Mis Rifas' : 'Ver Rifas Finalizadas'}
                <span className="bg-gray-500 text-white px-2 py-1 rounded-full text-xs">
                  {misRifasFinalizadas.length}
                </span>
            </button>
              {!showFinalizadas && (
            <button
                  onClick={() => navigateTo('/gestionar-rifas')}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-bold flex items-center gap-3 transition shadow-lg"
            >
                  <User className="w-5 h-5" />
                  Ver Compradores
            </button>
          )}
        </div>
          </div>
        </div>
                  
        {/* Sección de Pagos Pendientes Global */}
        {(() => {
          const rifasConPendientes = misRifasActivas.filter(rifa => (rifa.numerosPendientes || []).length > 0);
          const totalPendientes = rifasConPendientes.reduce((total, rifa) => total + (rifa.numerosPendientes?.length || 0), 0);
          
          if (totalPendientes > 0) {
            return (
              <div className="bg-orange-900 bg-opacity-50 rounded-xl p-6 border border-orange-400 mb-8">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-orange-400">⏳</span>
                    Pagos Pendientes de Confirmación
                  </h3>
                <p className="text-orange-200 mb-4">
                  Tienes <span className="font-bold text-yellow-400">{totalPendientes}</span> números pendientes de confirmación en {rifasConPendientes.length} rifa(s)
                </p>
                
                <div className="grid gap-4">
                  {rifasConPendientes.map(rifa => {
                    const comprasPendientes = compras.filter(c => 
                      c.rifaId === rifa.id && 
                      c.estado === 'pendiente' &&
                      c.numeros.some(num => (rifa.numerosPendientes || []).includes(num))
                    );

    return (
                      <div key={rifa.id} className="bg-orange-800 bg-opacity-50 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                    <div>
                            <h4 className="text-white font-bold text-lg">{rifa.titulo}</h4>
                            <p className="text-orange-200 text-sm">
                              {rifa.numerosPendientes?.length || 0} números pendientes
                            </p>
                    </div>
        <button
                            onClick={() => handleVerRifa(rifa)}
                            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition"
        >
                            Gestionar
        </button>
                    </div>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {(rifa.numerosPendientes || []).map(num => (
                            <span key={num} className="bg-orange-500 text-white px-3 py-1 rounded-lg font-bold">
                            {num}
                          </span>
                        ))}
                      </div>
                        
                        <div className="text-orange-200 text-sm">
                          Total pendiente: ${(rifa.numerosPendientes?.length || 0) * parseFloat(rifa.precio)}
                </div>
                </div>
                    );
                  })}
                </div>
                </div>
            );
          }
          return null;
        })()}

        {showFinalizadas ? (
          // Mostrar rifas finalizadas
          misRifasFinalizadas.length === 0 ? (
            <div className="text-center py-16 bg-white bg-opacity-10 backdrop-blur-md rounded-xl border border-purple-400">
              <Ticket className="w-24 h-24 text-gray-400 mx-auto mb-4 opacity-50" />
              <p className="text-white text-xl mb-2">No tienes rifas finalizadas aún</p>
              <p className="text-purple-300">Las rifas finalizadas aparecerán aquí</p>
            </div>
          ) : (
            <div className="space-y-6">
              {misRifasFinalizadas.map(rifa => {
                const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
                const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
                
                return (
                  <div key={rifa.id} className="bg-gray-600 bg-opacity-20 backdrop-blur-md rounded-xl p-6 border border-gray-500 opacity-75">
                    <div className="flex justify-between items-start mb-6">
                <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-400">{rifa.titulo}</h3>
                          <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                            ✅ Finalizada
                          </span>
                </div>
                        <p className="text-gray-300 mb-2">{rifa.descripcion}</p>
                        <p className="text-gray-400 text-sm">
                          Creada el {new Date(rifa.fechaCreacion).toLocaleDateString()}
                        </p>
              </div>
            </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-2">Premio</p>
                        <div className="text-gray-300 font-bold space-y-1">
                          {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                            <div key={index} className="text-sm">{index + 1}° {premio}</div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Precio</p>
                        <p className="text-gray-300 font-bold">${rifa.precio}</p>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Vendidos</p>
                        <p className="text-gray-300 font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                      </div>
                      <div className="bg-black bg-opacity-30 rounded-lg p-4">
                        <p className="text-gray-400 text-sm mb-1">Recaudado</p>
                        <p className="text-green-300 font-bold">${totalRecaudado}</p>
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-500 pt-4">
                      <span className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg font-semibold cursor-not-allowed inline-block">
                        Rifa Finalizada
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : misRifasActivas.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-blur-md rounded-xl border border-purple-400">
            <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">¡Hola {user?.name || 'Usuario'}! No has creado ninguna rifa aún</p>
            <p className="text-purple-300 mb-6">¡Crea tu primera rifa y comienza a vender números!</p>
            <button
              onClick={() => setShowCreateRifa(true)}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold inline-flex items-center gap-2 transition"
            >
              <Plus className="w-5 h-5" />
              Crear Mi Primera Rifa
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {misRifasActivas.map(rifa => {
              const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
              const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
              const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
              
              return (
                <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-6 border border-purple-400">
                  <div className="flex justify-between items-start mb-6">
            <div>
                      <h3 className="text-2xl font-bold text-yellow-400 mb-2">{rifa.titulo}</h3>
                      <p className="text-white mb-2">{rifa.descripcion}</p>
                      <p className="text-purple-300 text-sm">
                        Creada el {new Date(rifa.fechaCreacion).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleVerRifa(rifa)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition"
                    >
                      Ver Detalles
                    </button>
                    
                    {!rifa.finalizada && rifa.numerosVendidos > 0 && (
                      <div className="flex flex-col gap-2">
                        {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                          const ganadoresRifa = ganadores.filter(g => g.rifaId === rifa.id);
                          const premioYaSorteado = ganadoresRifa.some(g => g.rifaPremio === premio);
                          
                          return (
                            <button
                              key={index}
                              onClick={() => {
                                setSelectedRifa(rifa);
                                setSelectedPremioForSorteo({ premio, index });
                                setShowSeleccionarPremioModal(true);
                              }}
                              disabled={premioYaSorteado}
                              className={`px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${
                                premioYaSorteado
                                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                              }`}
                            >
                              🎲 {premioYaSorteado ? `✓ Premio ${index + 1} Sortado` : `Sortear Premio ${index + 1}`}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    {rifa.finalizada && (
                      <span className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold">
                        ✅ Finalizada
                    </span>
                    )}
                    
                    {isAdmin() && (
                      <button
                        onClick={() => handleEliminarRifa(rifa.id)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                      >
                        🗑️ Eliminar
                      </button>
                    )}
                </div>
                  
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-black bg-opacity-30 rounded-lg p-4">
                <p className="text-purple-300 text-sm mb-2">Premio</p>
                <div className="text-white font-bold space-y-1">
                  {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                    <div key={index} className="text-sm">{index + 1}° {premio}</div>
                  ))}
                </div>
                  </div>
              <div className="bg-black bg-opacity-30 rounded-lg p-4">
                <p className="text-purple-300 text-sm mb-1">Precio</p>
                <p className="text-white font-bold">${rifa.precio}</p>
                  </div>
              <div className="bg-black bg-opacity-30 rounded-lg p-4">
                <p className="text-purple-300 text-sm mb-1">Vendidos</p>
                <p className="text-white font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                  </div>
              <div className="bg-black bg-opacity-30 rounded-lg p-4">
                <p className="text-purple-300 text-sm mb-1">Recaudado</p>
                <p className="text-green-400 font-bold">${totalRecaudado}</p>
                </div>
              </div>

            {/* Estados de Números */}
            <div className="bg-black bg-opacity-30 rounded-lg p-4 mb-6">
              <h4 className="text-white font-bold mb-3">Estado de Números</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-white text-sm">Disponibles: {parseInt(rifa.totalNumeros) - rifa.numerosVendidos - (rifa.numerosPendientes?.length || 0)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span className="text-white text-sm">Vendidos: {rifa.numerosVendidos}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-white text-sm">Pendientes: {rifa.numerosPendientes?.length || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-white text-sm">Total: {rifa.totalNumeros}</span>
                </div>
            </div>
          </div>

                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-white mb-2">
                      <span>Progreso de ventas</span>
                      <span className="font-bold">{porcentajeVendido.toFixed(1)}%</span>
              </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-yellow-500 h-3 rounded-full transition-all"
                        style={{ width: `${porcentajeVendido}%` }}
                      ></div>
            </div>
          </div>

            <div className="flex items-center justify-between pt-4 border-t border-purple-400">
              <div className="text-white">
                <p className="text-sm text-purple-300">Sorteo programado para:</p>
                <p className="font-bold text-lg">{new Date(rifa.fechaSorteo).toLocaleDateString()}</p>
                </div>
              <div className="text-white text-right">
                <p className="text-sm text-purple-300">Compradores:</p>
                <p className="font-bold text-lg">{comprasRifa.length}</p>
            </div>
          </div>

            {/* Botones de Acción Rápida */}
            {(rifa.numerosPendientes?.length || 0) > 0 && (
              <div className="mt-4 pt-4 border-t border-orange-400">
                <div className="bg-orange-900 bg-opacity-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-orange-200 font-bold">⏳ Números Pendientes</p>
                      <p className="text-orange-300 text-sm">
                        {rifa.numerosPendientes?.length || 0} números esperando confirmación
                      </p>
            </div>
                    <div className="text-right">
                      <p className="text-orange-200 font-bold">
                        ${(rifa.numerosPendientes?.length || 0) * parseFloat(rifa.precio)}
                      </p>
          </div>
        </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {(rifa.numerosPendientes || []).map(num => (
                      <span key={num} className="bg-orange-500 text-white px-3 py-1 rounded-lg font-bold">
                        {num}
                      </span>
                    ))}
                    </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (confirm(`¿Confirmar TODOS los pagos pendientes?\nNúmeros: ${(rifa.numerosPendientes || []).join(', ')}\nTotal: $${(rifa.numerosPendientes?.length || 0) * parseFloat(rifa.precio)}`)) {
                          handleConfirmarPago(rifa.id, rifa.numerosPendientes || []);
                        }
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Confirmar Todos
                    </button>
                    
                    <button
                      onClick={() => handleVerRifa(rifa)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                    >
                      Ver Detalles
                    </button>
                  </div>
                </div>
              </div>
            )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    );
  };

  // Admin Panel - Gestión de usuarios y rifas
  const AdminPanelPage = () => {
    // Excluir al admin de la lista de usuarios (comparar por id en lugar de uid)
    const regularUsers = users.filter(u => u.id !== 'Trz2xMBhVjc9pnOOGkTPegeNGGD2');
    
    // Eliminar duplicados por email, manteniendo solo el primer registro
    const uniqueUsers = regularUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.email === user.email)
    );
    
    const pendingUsers = uniqueUsers.filter(u => !u.pagoRealizado);
    const approvedUsers = uniqueUsers.filter(u => u.pagoRealizado);
    
    console.log('👥 Admin Panel Debug:', {
      totalUsers: users.length,
      regularUsers: regularUsers.length,
      pendingUsers: pendingUsers.length,
      approvedUsers: approvedUsers.length,
      usersList: users.map(u => ({ 
        name: u.nombre || u.username, 
        email: u.email, 
        uid: u.uid,
        pagoRealizado: u.pagoRealizado 
      }))
    });
    
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
          <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al inicio
          </button>

        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-purple-400 mb-6">
          <h2 className="text-3xl font-bold text-yellow-400 mb-6">🔐 Panel de Administración</h2>
          <p className="text-white text-lg mb-6">Gestiona usuarios y sus permisos para crear rifas</p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Usuarios Pendientes</p>
              <p className="text-orange-400 font-bold text-3xl">{pendingUsers.length}</p>
          </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Usuarios Aprobados</p>
              <p className="text-green-400 font-bold text-3xl">{approvedUsers.length}</p>
                  </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Total Usuarios</p>
              <p className="text-white font-bold text-3xl">{uniqueUsers.length}</p>
                  </div>
                  </div>
                  </div>
                  
        {/* Sección de Usuarios Pendientes */}
        {pendingUsers.length > 0 && (
          <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-xl p-8 border border-orange-400 mb-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="text-orange-400">⏳</span>
              Usuarios Pendientes de Aprobación ({pendingUsers.length})
            </h3>
            
            <div className="space-y-4">
              {pendingUsers.map(pendingUser => (
                <div key={pendingUser.id} className="bg-orange-900 bg-opacity-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-bold text-lg">{pendingUser.nombre || pendingUser.username}</h4>
                      <p className="text-orange-200 text-sm">{pendingUser.email}</p>
                      <p className="text-orange-300 text-sm">Registrado: {new Date(pendingUser.fechaRegistro).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-orange-200 text-sm">Estado</p>
                      <p className="text-orange-400 font-bold">Pendiente</p>
                    </div>
                </div>
                
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        console.log('🔍 Clicked approve for user:', pendingUser);
                        const rifasPermitidas = prompt('¿Cuántas rifas puede crear este usuario? (0 = ilimitado)');
                        if (rifasPermitidas !== null) {
                          const cantidad = rifasPermitidas === '0' ? 999 : parseInt(rifasPermitidas);
                          if (!isNaN(cantidad) && cantidad > 0) {
                            handleApproveUser(pendingUser.id, cantidad);
                          } else {
                            alert('Por favor ingresa un número válido');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar Usuario
                    </button>
                    
                    <button
                      onClick={() => handleRejectUser(pendingUser.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Rechazar
                    </button>
                </div>
                </div>
                        ))}
                      </div>
                    </div>
        )}

        {/* Sección de Usuarios Aprobados */}
        {approvedUsers.length > 0 && (
          <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-green-400 mb-6">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="text-green-400">✅</span>
              Usuarios Aprobados ({approvedUsers.length})
            </h3>
            
                        <div className="space-y-4">
              {approvedUsers.map(approvedUser => (
                <div key={approvedUser.id} className="bg-green-900 bg-opacity-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-white font-bold text-lg">{approvedUser.nombre || approvedUser.username}</h4>
                      <p className="text-green-200 text-sm">{approvedUser.email}</p>
                      <p className="text-green-300 text-sm">
                        Rifas permitidas: {approvedUser.rifasPermitidas === 999 ? 'Ilimitadas' : approvedUser.rifasPermitidas}
                      </p>
                      <p className="text-green-300 text-sm">
                        Aprobado: {new Date(approvedUser.fechaPago).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-200 text-sm">Estado</p>
                      <p className="text-green-400 font-bold">Aprobado</p>
                  </div>
                    </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        const nuevasRifas = prompt(`Rifas permitidas actuales: ${approvedUser.rifasPermitidas}\n\n¿Cuántas rifas puede crear? (0 = ilimitado)`);
                        if (nuevasRifas !== null) {
                          const cantidad = nuevasRifas === '0' ? 999 : parseInt(nuevasRifas);
                          if (!isNaN(cantidad) && cantidad >= 0) {
                            handleApproveUser(approvedUser.id, cantidad);
                          } else {
                            alert('Por favor ingresa un número válido');
                          }
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Modificar Permisos
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensaje si no hay usuarios */}
        {regularUsers.length === 0 && (
          <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-purple-400 text-center">
            <div className="text-6xl mb-4">👥</div>
            <p className="text-white text-xl mb-2">No hay usuarios registrados</p>
            <p className="text-purple-300">Los usuarios aparecerán aquí cuando se registren</p>
          </div>
        )}
      </main>
    );
  };

  // Gestionar Rifas Page - Ver compradores de cada rifa
  const GestionarRifasPage = () => {
    const [selectedRifaId, setSelectedRifaId] = useState(null);
    // Filtrar rifas por creadorId - comparar tanto con user.id como con user.uid
    const misRifas = rifas.filter(r => 
      r.creadorId === user?.id || 
      r.creadorId === user?.uid || 
      (r.creadorNombre && user?.name && r.creadorNombre.toLowerCase() === user.name.toLowerCase())
    );
    
    // Solo mostrar rifas que tienen compras
    const rifasConCompras = misRifas.filter(rifa => {
      const comprasRifa = compras.filter(c => c.rifaId === rifa.id);
      return comprasRifa.length > 0;
    });

    const toggleRifa = (rifaId) => {
      setSelectedRifaId(selectedRifaId === rifaId ? null : rifaId);
    };

    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
                    <button
          onClick={() => navigateTo('/mis-rifas')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
                    >
          <ArrowLeft className="w-5 h-5" />
          Volver a Mis Rifas
                    </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-purple-400 mb-6">
          <h2 className="text-3xl font-bold text-yellow-400 mb-2">👥 Gestionar Rifas y Compradores</h2>
          <p className="text-white text-lg">
            Revisa los compradores de cada rifa
          </p>
                  </div>

        {rifasConCompras.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
            <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">No hay rifas con compradores aún</p>
            <p className="text-purple-300">Las rifas con compras aparecerán aquí</p>
          </div>
        ) : (
          <div className="space-y-6">
            {rifasConCompras.map(rifa => {
              const compradoresRifa = compras.filter(c => c.rifaId === rifa.id);
              console.log('🔍 Rifa:', rifa.titulo);
              console.log('📊 Compras encontradas:', compradoresRifa);
              console.log('📂 Todas las compras:', compras);
              
              const compradoresConfirmados = compradoresRifa.filter(c => c.estado === 'confirmado');
              const compradoresPendientes = compradoresRifa.filter(c => c.estado === 'pendiente');
              const isExpanded = selectedRifaId === rifa.id;
              
              return (
                <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400 overflow-hidden">
                  {/* Header clickeable */}
                  <div 
                    onClick={() => toggleRifa(rifa.id)}
                    className="p-6 cursor-pointer hover:bg-white hover:bg-opacity-5 transition"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-yellow-400">{rifa.titulo}</h3>
                          <span className="px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
                            {rifa.tipo === 'rapadita' ? '⚡ Rapadita' : '🎫 Rifa'}
                          </span>
                          <span className={`px-3 py-1 ${isExpanded ? 'bg-green-600' : 'bg-blue-600'} text-white text-xs font-semibold rounded-full`}>
                            {isExpanded ? '▼ Ver Menos' : '▶ Ver Compradores'}
                          </span>
                        </div>
                        <p className="text-white text-sm mb-3">{rifa.descripcion}</p>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-black bg-opacity-40 rounded-lg p-3">
                            <p className="text-purple-300 text-xs mb-1">Total Compradores</p>
                            <p className="text-white font-bold text-xl">{compradoresRifa.length}</p>
                          </div>
                          <div className="bg-green-900 bg-opacity-40 rounded-lg p-3">
                            <p className="text-green-300 text-xs mb-1">Confirmados</p>
                            <p className="text-green-400 font-bold text-xl">{compradoresConfirmados.length}</p>
                          </div>
                          <div className="bg-orange-900 bg-opacity-40 rounded-lg p-3">
                            <p className="text-orange-300 text-xs mb-1">Pendientes</p>
                            <p className="text-orange-400 font-bold text-xl">{compradoresPendientes.length}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contenido expandible */}
                  {isExpanded && compradoresRifa.length > 0 && (
                    <div className="border-t border-purple-400 bg-black bg-opacity-20 p-6">
                      <h4 className="text-lg font-bold text-white mb-4">📋 Lista de Compradores</h4>
                      
                      {compradoresConfirmados.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-md font-semibold text-green-400 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Compradores Confirmados ({compradoresConfirmados.length})
                          </h5>
                          <div className="space-y-3">
                            {compradoresConfirmados.map(compra => (
                              <div key={compra.id} className="bg-green-900 bg-opacity-30 rounded-lg p-4 border border-green-500">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">{compra.compradorNombre}</p>
                                    <p className="text-green-200 text-xs">ID Compra: {compra.id}</p>
                                    <p className="text-green-200 text-sm">📍 {compra.compradorDireccion}</p>
                                    <p className="text-green-200 text-sm">📧 {compra.compradorEmail}</p>
                                    <p className="text-green-200 text-sm">📱 {compra.compradorTelefono}</p>
                                  </div>
                                  <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                                    Confirmado
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  <span className="text-purple-300 text-sm font-semibold">Números:</span>
                                  {compra.numeros.sort((a, b) => a - b).map(num => (
                                    <span key={num} className="bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">
                                      {num}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-2 text-right">
                                  <span className="text-green-300 font-bold">Total: ${compra.total}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {compradoresPendientes.length > 0 && (
                        <div>
                          <h5 className="text-md font-semibold text-orange-400 mb-3 flex items-center gap-2">
                            <span className="text-orange-400">⏳</span>
                            Compradores Pendientes ({compradoresPendientes.length})
                          </h5>
                          <div className="space-y-3">
                            {compradoresPendientes.map(compra => (
                              <div key={compra.id} className="bg-orange-900 bg-opacity-30 rounded-lg p-4 border border-orange-500">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="text-white font-bold text-lg">{compra.compradorNombre}</p>
                                    <p className="text-orange-200 text-xs">ID Compra: {compra.id}</p>
                                    <p className="text-orange-200 text-sm">📍 {compra.compradorDireccion}</p>
                                    <p className="text-orange-200 text-sm">📧 {compra.compradorEmail}</p>
                                    <p className="text-orange-200 text-sm">📱 {compra.compradorTelefono}</p>
                                  </div>
                                  <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded-full">
                                    Pendiente
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  <span className="text-purple-300 text-sm font-semibold">Números:</span>
                                  {compra.numeros.sort((a, b) => a - b).map(num => (
                                    <span key={num} className="bg-orange-500 text-white px-2 py-1 rounded text-sm font-bold">
                                      {num}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-2 text-right">
                                  <span className="text-orange-300 font-bold">Total: ${compra.total}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    );
  };

  // Todas las Rifas Page - Solo para admin
  const TodasLasRifasPage = () => {
    // Mostrar TODAS las rifas (admin puede verlas todas)
    const allRifas = rifas;
    
    return (
    <main className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al inicio
        </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-purple-400 mb-6">
          <h2 className="text-3xl font-bold text-yellow-400 mb-6">🎫 Todas las Rifas Creadas</h2>
          <p className="text-white text-lg mb-6">Revisa y gestiona todas las rifas creadas por usuarios autorizados</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Total de Rifas</p>
              <p className="text-purple-400 font-bold text-3xl">{allRifas.length}</p>
            </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Rifas Activas</p>
              <p className="text-green-400 font-bold text-3xl">{allRifas.filter(r => !r.finalizada).length}</p>
            </div>
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <p className="text-purple-300 text-sm mb-2">Rifas Finalizadas</p>
              <p className="text-orange-400 font-bold text-3xl">{allRifas.filter(r => r.finalizada).length}</p>
            </div>
          </div>
      </div>
      
        {allRifas.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
          <Ticket className="w-24 h-24 text-purple-400 mx-auto mb-4 opacity-50" />
            <p className="text-white text-xl mb-2">No hay rifas creadas</p>
            <p className="text-purple-300">Los usuarios aún no han creado ninguna rifa</p>
        </div>
      ) : (
          <div className="space-y-6">
            {allRifas.map(rifa => {
              const creador = users.find(u => u.id === rifa.creadorId);
              const isAutorizada = creador && creador.pagoRealizado === true;
            const porcentajeVendido = (rifa.numerosVendidos / parseInt(rifa.totalNumeros)) * 100;
              const totalRecaudado = rifa.numerosVendidos * parseFloat(rifa.precio);
              
            return (
                <div key={rifa.id} className="bg-white bg-opacity-10 backdrop-md rounded-xl p-6 border border-purple-400">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-2xl font-bold text-yellow-400">{rifa.titulo}</h3>
                        {isAutorizada ? (
                          <span className="px-3 py-1 bg-green-700 text-white text-sm font-semibold rounded-full">
                            ✅ Autorizada
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-orange-600 text-white text-sm font-semibold rounded-full">
                            ⚠️ Pendiente de Autorización
                          </span>
                        )}
                        {rifa.finalizada && (
                          <span className="px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-full">
                            ✅ Finalizada
                          </span>
                        )}
                      </div>
                <p className="text-white mb-4">{rifa.descripcion}</p>
                      <div className="flex items-center gap-4 text-sm text-purple-300">
                        <span>Creada por: <span className="text-white font-semibold">{creador?.nombre || creador?.username || 'Usuario desconocido'}</span></span>
                        <span>•</span>
                        <span>{new Date(rifa.fechaCreacion).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleVerRifa(rifa)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
                    >
                      Ver Detalles
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-2">Premio</p>
                      <div className="text-white font-bold space-y-1">
                        {(rifa.premios || (rifa.premio ? rifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => (
                          <div key={index} className="text-sm">{index + 1}° {premio}</div>
                        ))}
                  </div>
                  </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Precio</p>
                      <p className="text-white font-bold">${rifa.precio}</p>
                  </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Vendidos</p>
                      <p className="text-white font-bold">{rifa.numerosVendidos}/{rifa.totalNumeros}</p>
                    </div>
                    <div className="bg-black bg-opacity-30 rounded-lg p-4">
                      <p className="text-purple-300 text-sm mb-1">Recaudado</p>
                      <p className="text-green-400 font-bold">${totalRecaudado}</p>
                  </div>
                </div>

                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-white mb-2">
                      <span>Progreso de ventas</span>
                      <span className="font-bold">{porcentajeVendido.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-green-500 to-yellow-500 h-3 rounded-full transition-all" 
                      style={{ width: `${porcentajeVendido}%` }}
                    ></div>
                  </div>
                </div>
                
                  <div className="flex gap-3 pt-4 border-t border-purple-400">
                <button 
                      onClick={() => handleEliminarRifa(rifa.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition flex items-center gap-2"
                >
                      🗑️ Eliminar Rifa
                </button>
                    {creador && (
                      <div className="flex items-center gap-2 text-white ml-auto">
                        <User className="w-4 h-4" />
                        <span className="text-sm">{creador.email}</span>
                      </div>
                    )}
                  </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
  };

  // Habilitar Rifa Page
  const HabilitarRifaPage = () => {
    // Recargar automáticamente cada 10 segundos
    useEffect(() => {
      const interval = setInterval(async () => {
        try {
          const usersData = await loadUsersFromFirestore();
          setUsers(usersData);
          console.log('🔄 Usuarios recargados automáticamente');
        } catch (error) {
          console.error('Error al recargar estado:', error);
        }
      }, 10000); // 10 segundos

      return () => clearInterval(interval);
    }, []);

    const handleRecargarEstado = async () => {
      try {
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);
        alert('✅ Estado actualizado');
      } catch (error) {
        console.error('Error al recargar estado:', error);
        alert('❌ Error al recargar estado');
      }
    };

    const handleSolicitarHabilitacion = async () => {
      if (!user) {
        alert('❌ Debes iniciar sesión para solicitar habilitación');
        navigateTo('/login');
        return;
      }

      try {
        // Verificar si el usuario ya existe en la base de datos
        const userData = users.find(u => u.id === user.id);
        
        if (userData && userData.pagoRealizado) {
          alert('✅ Ya estás habilitado para crear rifas');
          return;
        }

        if (userData && userData.solicitudPendiente) {
          alert('⏳ Ya tienes una solicitud pendiente. Espere a que el administrador la apruebe.');
          return;
        }

        // Guardar solicitud en Firestore
        const docRef = await addDoc(collection(db, 'solicitudes_rifas'), {
          userId: user.id,
          nombreUsuario: user.name,
          emailUsuario: user.email,
          estado: 'pendiente',
          fechaSolicitud: new Date().toISOString(),
          rifasSolicitadas: 1
        });

        // Actualizar el usuario en la colección usuarios
        if (userData) {
          const userRef = doc(db, 'usuarios', userData.id);
          await updateDoc(userRef, {
            solicitudPendiente: true,
            fechaSolicitud: new Date().toISOString()
          });
        } else {
          // Crear el usuario si no existe
          const newUserData = {
            id: user.id,
            uid: user.uid,
            nombre: user.name,
            username: user.name,
            email: user.email,
            fechaRegistro: new Date().toISOString(),
            solicitudPendiente: true,
            fechaSolicitud: new Date().toISOString(),
            pagoRealizado: false,
            rifasPermitidas: 0
          };
          
          // Solo agregar foto si existe
          if (user.photoURL) {
            newUserData.foto = user.photoURL;
          }
          
          await addDoc(collection(db, 'usuarios'), newUserData);
        }

        // Recargar usuarios
        const usersData = await loadUsersFromFirestore();
        setUsers(usersData);

        alert('✅ Solicitud enviada exitosamente.\n\nEl administrador revisará tu solicitud y te habilitará para crear rifas.');
        
      } catch (error) {
        console.error('Error al enviar solicitud:', error);
        alert('❌ Error al enviar la solicitud. Por favor intenta de nuevo.');
      }
    };

    // Buscar usuario por ID, UID o email
    const userData = users.find(u => 
      u.id === user?.id || 
      u.uid === user?.uid || 
      u.email === user?.email
    );
    
    const yaHabilitado = userData && userData.pagoRealizado;
    const solicitudPendiente = userData && userData.solicitudPendiente;

    // Debug logs
    console.log('🔍 HABILITAR RIFA DEBUG:', {
      currentUser: user,
      userData: userData,
      usersList: users.map(u => ({
        id: u.id,
        uid: u.uid,
        email: u.email,
        nombre: u.nombre,
        pagoRealizado: u.pagoRealizado,
        solicitudPendiente: u.solicitudPendiente
      })),
      yaHabilitado,
      solicitudPendiente
    });

    return (
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigateTo('/inicio')}
          className="flex items-center gap-2 text-white hover:text-yellow-400 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al inicio
        </button>

        <div className="bg-white bg-opacity-10 backdrop-md rounded-xl p-8 border border-purple-400 mb-6">
          <h2 className="text-3xl font-bold text-yellow-400 mb-4">💳 Solicitud de Habilitación para Crear Rifas</h2>
          <p className="text-white text-lg mb-6">Para crear rifas, debes pagar $3000 ARS y enviar el comprobante</p>
          
          <div className="bg-green-900 bg-opacity-30 border-2 border-green-500 rounded-lg p-4 mb-6">
            <p className="text-green-200 text-center font-semibold">
              💰 Precio por habilitación: $3,000 ARS
            </p>
        </div>
      </div>

        {/* Información de Pago */}
        <div className="bg-gradient-to-br from-green-900 to-green-800 rounded-xl p-8 border-2 border-green-500 mb-6">
          <h3 className="text-2xl font-bold text-yellow-400 mb-6 text-center">📋 Datos para Transferencia</h3>
          
          <div className="space-y-4">
            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-green-200 font-semibold">Titular:</span>
                <span className="text-white font-bold text-lg">Lucio Marcelino Tapia</span>
              </div>
            </div>

            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-200 font-semibold">Alias:</span>
                <span className="text-white font-bold text-xl">acceso7</span>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('acceso7');
                  alert('✅ Alias copiado al portapapeles');
                }}
                className="text-yellow-400 text-sm hover:underline"
              >
                📋 Copiar Alias
              </button>
            </div>

            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-green-200 font-semibold">CBU:</span>
                <span className="text-white font-bold text-lg">3840200500000004585363</span>
              </div>
            <button
                onClick={() => {
                  navigator.clipboard.writeText('3840200500000004585363');
                  alert('✅ CBU copiado al portapapeles');
                }}
                className="text-yellow-400 text-sm hover:underline"
              >
                📋 Copiar CBU
            </button>
            </div>

            <div className="bg-black bg-opacity-40 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <span className="text-green-200 font-semibold">Banco:</span>
                <span className="text-white font-bold">Ualá Bank S.A.U.</span>
              </div>
              </div>
            </div>

          <div className="mt-6 bg-yellow-900 bg-opacity-50 border-2 border-yellow-500 rounded-lg p-4">
            <p className="text-yellow-200 text-center font-semibold">
              ⚠️ IMPORTANTE: Después de realizar la transferencia, envía el comprobante por WhatsApp
            </p>
              </div>
        </div>

        {/* Botón de WhatsApp */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-6 mb-6 border-2 border-green-400">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-5xl">📱</div>
              <div>
                <p className="text-white font-bold text-xl">Enviar Comprobante por WhatsApp</p>
                <p className="text-green-200 text-sm">Haz clic para abrir WhatsApp</p>
              </div>
            </div>
            <a
              href="https://wa.me/5493872198309"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-green-700 font-bold rounded-lg hover:bg-green-100 transition flex items-center gap-2 text-lg"
            >
              📱 WhatsApp
            </a>
          </div>
        </div>

        {/* Estado del Usuario */}
        {yaHabilitado ? (
          <div className="bg-green-900 bg-opacity-50 border-2 border-green-500 rounded-xl p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-bold text-green-400 mb-4">¡Ya estás habilitado!</h3>
            <p className="text-white mb-2">Puedes crear rifas sin restricciones.</p>
            {userData.rifasPermitidas && (
              <p className="text-green-300">
                Rifas permitidas: {userData.rifasPermitidas === 999 ? 'Ilimitadas' : userData.rifasPermitidas}
              </p>
            )}
          </div>
        ) : solicitudPendiente ? (
          <div className="bg-orange-900 bg-opacity-50 border-2 border-orange-500 rounded-xl p-8 text-center">
            <div className="text-6xl mb-4">⏳</div>
            <h3 className="text-2xl font-bold text-orange-400 mb-4">Solicitud Pendiente</h3>
            <p className="text-white mb-2">Tu solicitud está siendo revisada por el administrador.</p>
            <p className="text-orange-300 mb-4">Recibirás una notificación cuando sea aprobada.</p>
                <button
              onClick={handleRecargarEstado}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition flex items-center gap-2 mx-auto"
                >
              🔄 Recargar Estado
                </button>
            <p className="text-orange-200 text-sm mt-2">Haz clic para verificar si tu solicitud fue aprobada</p>
          </div>
        ) : (
          <div className="text-center">
                <button
              onClick={handleSolicitarHabilitacion}
              className="px-12 py-5 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold rounded-xl transition text-xl shadow-2xl flex items-center gap-3 mx-auto"
            >
              <CheckCircle className="w-7 h-7" />
              Solicitar Habilitación para Crear Rifas
                </button>
            <p className="text-purple-300 text-sm mt-4">
              * Al hacer clic, se registra tu solicitud y aparecerá como pendiente en el panel de administración.
            </p>
              </div>
        )}
      </main>
    );
  };

  // Ganadores Page
  const GanadoresPage = () => {
    return (
    <main className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-white mb-8 text-center">🏆 Ganadores de Rifas</h2>
        
        {ganadores.length === 0 ? (
          <div className="text-center py-16 bg-white bg-opacity-10 backdrop-md rounded-xl border border-purple-400">
            <div className="text-6xl mb-4">🎉</div>
            <p className="text-white text-xl mb-2">Aún no hay ganadores</p>
            <p className="text-purple-300">Los ganadores aparecerán aquí una vez se realicen los sorteos</p>
            </div>
      ) : (
          <div className="space-y-6">
            {ganadores.map(ganador => {
              const rifa = rifas.find(r => r.id === ganador.rifaId);
            return (
                <div key={ganador.id} className="bg-gradient-to-r from-yellow-600 to-yellow-700 rounded-xl p-8 border-4 border-yellow-400 shadow-2xl">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="text-3xl font-bold text-white mb-2">🎊 {ganador.rifaTitulo}</h3>
                      <p className="text-yellow-100 text-lg">Premio: {ganador.rifaPremio}</p>
          </div>
                    <div className="text-right">
                      <p className="text-yellow-100 text-sm">Fecha del sorteo</p>
                      <p className="text-white font-bold">{new Date(ganador.fechaSorteo).toLocaleString()}</p>
        </div>
                  </div>

                  <div className="bg-white bg-opacity-20 rounded-lg p-6 mb-6">
                    <div className="text-center mb-4">
                      <p className="text-yellow-100 text-lg mb-2">Número Ganador</p>
                      <div className="inline-block bg-white text-yellow-700 text-6xl font-bold px-8 py-4 rounded-xl shadow-lg">
                        {ganador.numeroGanador}
                      </div>
                  </div>
                </div>

                  <div className="bg-white bg-opacity-20 rounded-lg p-6">
                    <p className="text-yellow-100 text-lg mb-3 flex items-center gap-2">
                      <Trophy className="w-6 h-6" />
                      Ganador/a:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-white font-bold text-2xl mb-3">{ganador.ganadorNombre}</p>
                        {/* Buscar la compra para obtener más datos */}
                        {(() => {
                          const compraGanadora = compras.find(c => 
                            c.rifaId === ganador.rifaId && 
                            c.numeros.includes(ganador.numeroGanador)
                          );
                          
                          if (compraGanadora && user && user.id === ganador.creadorId) {
                            return (
                              <div className="space-y-2">
                                <div>
                                  <p className="text-yellow-100 text-sm">Dirección:</p>
                                  <p className="text-white">{compraGanadora.compradorDireccion}</p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                  </div>
                      <div className="text-right">
                        {user && user.id === ganador.creadorId && (
                          <div className="space-y-1">
                            <p className="text-yellow-100 text-sm mb-2">Datos de contacto:</p>
                            <a 
                              href={`mailto:${ganador.ganadorEmail}`}
                              className="block text-white hover:text-yellow-200 transition flex items-center justify-end gap-1"
                            >
                              <Mail className="w-4 h-4" />
                              {ganador.ganadorEmail}
                            </a>
                            <a 
                              href={`https://wa.me/${ganador.ganadorTelefono.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-white hover:text-yellow-200 transition flex items-center justify-end gap-1"
                            >
                              <Phone className="w-4 h-4" />
                              {ganador.ganadorTelefono}
                            </a>
                </div>
                        )}
                      </div>
                </div>
                
                    {user && user.id === ganador.creadorId && (
                      <div className="mt-4 pt-4 border-t border-white border-opacity-20 flex gap-2">
                        <a
                          href={`mailto:${ganador.ganadorEmail}?subject=¡Felicitaciones! Ganaste: ${ganador.rifaPremio}&body=Hola ${ganador.ganadorNombre},%0D%0A%0D%0A¡Felicitaciones! Has ganado la rifa "${ganador.rifaTitulo}".%0D%0APremio: ${ganador.rifaPremio}%0D%0ANúmero ganador: ${ganador.numeroGanador}%0D%0A%0D%0APor favor contáctame para coordinar la entrega del premio.`}
                          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition text-center"
                        >
                          📧 Notificar por Email
                        </a>
                        <a
                          href={`https://wa.me/${ganador.ganadorTelefono.replace(/[^0-9]/g, '')}?text=🎉 ¡Felicitaciones ${ganador.ganadorNombre}! Has ganado la rifa "${ganador.rifaTitulo}". Premio: ${ganador.rifaPremio}. Número ganador: ${ganador.numeroGanador}. Contáctame para coordinar la entrega.`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition text-center"
                        >
                          💬 Notificar por WhatsApp
                        </a>
                      </div>
                    )}
                  </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
  };

  // Modal de Sorteo
  const SorteoModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-12 max-w-2xl w-full border-4 border-yellow-400 text-center">
        <h2 className="text-4xl font-bold text-white mb-8">
          {sorteoEnCurso ? '🎲 Sorteando...' : '🎉 ¡Tenemos un Ganador!'}
        </h2>
        
        <div className="bg-white bg-opacity-20 rounded-xl p-12 mb-8">
          <p className="text-yellow-400 text-2xl mb-4">Número Ganador</p>
          <div className={`text-9xl font-bold text-white transition-all ${sorteoEnCurso ? 'animate-pulse' : 'scale-110'}`}>
            {numeroGanador || '?'}
          </div>
        </div>

        {!sorteoEnCurso && numeroGanador && (
          <>
            <div className="mb-6">
              <p className="text-white text-xl mb-2">Premio:</p>
              <p className="text-yellow-400 text-3xl font-bold">{selectedRifa.premio}</p>
            </div>
            
            <button
              onClick={() => {
                setShowSorteoModal(false);
                setCurrentPage('ganadores');
              }}
              className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white text-xl font-bold rounded-lg transition"
            >
              Ver Ganadores
            </button>
          </>
        )}
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      <Header />
      
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'rifa-detail' && <RifaDetailPage />}
      {currentPage === 'datos-personales' && <DatosPersonalesPage />}
      {currentPage === 'payment' && <PaymentPage />}
      {currentPage === 'mis-rifas' && user && <MisRifasPage />}
      {currentPage === 'admin-panel' && user && <AdminPanelPage />}
      {currentPage === 'todas-las-rifas' && user && <TodasLasRifasPage />}
      {currentPage === 'gestionar-rifas' && user && <GestionarRifasPage />}
      {currentPage === 'habilitar-rifa' && <HabilitarRifaPage />}
      {currentPage === 'ganadores' && <GanadoresPage />}

      {showSorteoModal && <SorteoModal />}

      {/* Modal de Selección de Premio */}
      {showSeleccionarPremioModal && selectedPremioForSorteo && selectedRifa && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-yellow-400">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">🎲 Confirmar Sorteo</h2>
              <p className="text-purple-300">¿Estás seguro de que quieres sortear este premio?</p>
            </div>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div>
                  <p className="text-purple-300 text-sm mb-1">Rifa</p>
                  <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-2">Premio a Sortear</p>
                  <select
                    value={selectedPremioForSorteo.premio}
                    onChange={(e) => {
                      const premios = selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : []);
                      const selectedIndex = premios.indexOf(e.target.value);
                      setSelectedPremioForSorteo({ premio: e.target.value, index: selectedIndex });
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-yellow-400 focus:border-yellow-400 focus:outline-none font-semibold text-lg"
                  >
                    {(selectedRifa.premios || (selectedRifa.premio ? selectedRifa.premio.split(',').map(p => p.trim()) : [])).map((premio, index) => {
                      const ganadoresRifa = ganadores.filter(g => g.rifaId === selectedRifa.id);
                      const premioYaSorteado = ganadoresRifa.some(g => g.rifaPremio === premio);
                      
                      return (
                        <option 
                          key={index} 
                          value={premio}
                          disabled={premioYaSorteado}
                          className={premioYaSorteado ? 'bg-gray-600 text-gray-400' : 'bg-purple-800 text-white'}
                        >
                          {premioYaSorteado ? `✓ ${premio} (Ya sorteado)` : `${index + 1}. ${premio}`}
                        </option>
                      );
                    })}
                  </select>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-1">Números Disponibles</p>
                  <p className="text-white font-semibold">{selectedRifa.numerosReservados.length}</p>
              </div>
                </div>
              </div>
            
            <div className="bg-yellow-900 bg-opacity-30 rounded-lg p-4 mb-6 border border-yellow-500">
              <p className="text-yellow-200 text-sm text-center">
                ⚠️ Al confirmar, se realizará el sorteo y se seleccionará un número ganador automáticamente.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  realizarSorteo(selectedPremioForSorteo.index);
                  setShowSeleccionarPremioModal(false);
                  setSelectedPremioForSorteo(null);
                }}
                className="flex-1 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition"
              >
                🎲 Confirmar y Sortear
              </button>
              <button
                onClick={() => {
                  setShowSeleccionarPremioModal(false);
                  setSelectedPremioForSorteo(null);
                }}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-lg transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Información del Comprador */}
      {showCompradorModal && compradorInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-yellow-400">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">📋 Información del Comprador</h2>
              <div className="bg-yellow-500 text-black px-4 py-2 rounded-full inline-block font-bold text-xl">
                Número: {compradorInfo.numero}
              </div>
            </div>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <div className="space-y-4">
              <div>
                  <p className="text-purple-300 text-sm mb-1">Nombre Completo</p>
                  <p className="text-white font-bold text-lg">{compradorInfo.nombre}</p>
              </div>
              <div>
                  <p className="text-purple-300 text-sm mb-1">Dirección</p>
                  <p className="text-white font-semibold">{compradorInfo.direccion}</p>
              </div>
              </div>
            </div>
            
                <button
              onClick={() => setShowCompradorModal(false)}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-bold rounded-lg transition"
                >
              Cerrar
                </button>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Iniciar Sesión</h2>
            
            {/* Botón de Google */}
                <button
              onClick={handleGoogleLogin}
              className="w-full py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-semibold flex items-center justify-center gap-3 mb-6 transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </button>

            {/* Enlace a Política de Privacidad */}
            <div className="text-center mb-6">
              <p className="text-purple-200 text-sm">
                Al continuar, aceptas nuestra{' '}
                <a 
                  href="https://acceso7-32ad3.web.app/politica-privacidad" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 underline font-semibold"
                >
                  Política de Privacidad
                </a>
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowLogin(false)}
                className="w-full py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                  Cancelar
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Registro</h2>
            
            {/* Botón de Google */}
            <button
              onClick={handleGoogleLogin}
              className="w-full py-3 bg-white hover:bg-gray-100 text-gray-800 rounded-lg font-semibold flex items-center justify-center gap-3 mb-6 transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </button>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowRegister(false)}
                className="w-full py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Rifa Modal */}
      {showCreateRifa && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-4 sm:p-8 max-w-2xl w-full border-2 border-purple-400 my-4 sm:my-8 max-h-[95vh] overflow-y-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6 text-center">Crear Nueva Rifa</h2>
            <div className="space-y-3 sm:space-y-4">
              {/* Selector de Tipo */}
              <div>
                <label className="block text-white mb-2 font-semibold">Tipo de Sorteo</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRifaForm({...rifaForm, tipo: 'rifa'})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      rifaForm.tipo === 'rifa'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white bg-opacity-20 text-white border border-purple-400'
                    }`}
                  >
                    🎫 Rifa
                  </button>
                  <button
                    type="button"
                    onClick={() => setRifaForm({...rifaForm, tipo: 'rapadita'})}
                    className={`flex-1 py-3 rounded-lg font-semibold transition ${
                      rifaForm.tipo === 'rapadita'
                        ? 'bg-yellow-500 text-black'
                        : 'bg-white bg-opacity-20 text-white border border-purple-400'
                    }`}
                  >
                    ⚡ Rapadita
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Título</label>
                <input
                  type="text"
                  value={rifaForm.titulo}
                  onChange={(e) => setRifaForm({...rifaForm, titulo: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                />
              </div>
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Descripción</label>
                <textarea
                  value={rifaForm.descripcion}
                  onChange={(e) => setRifaForm({...rifaForm, descripcion: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                  rows="3"
                />
              </div>
              {/* Premios */}
              <div>
                <label className="block text-white mb-2 font-semibold">Premios</label>
                {rifaForm.premios.map((premio, index) => (
                  <div key={index} className="mb-2 flex gap-2">
                    <div className="flex-1 flex items-center gap-1 sm:gap-2">
                      <span className="text-yellow-400 font-bold text-sm sm:text-lg w-6 sm:w-8">{index + 1}°</span>
                <input
                  type="text"
                        value={premio}
                        onChange={(e) => updatePremio(index, e.target.value)}
                        className="flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                        placeholder={`${index + 1}° Premio`}
                />
              </div>
                    {rifaForm.premios.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePremio(index)}
                        className="px-3 sm:px-4 py-2 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition text-sm sm:text-base"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPremio}
                  className="w-full py-2 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  Agregar Premio
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Precio por Número</label>
                  <input
                    type="number"
                    value={rifaForm.precio}
                    onChange={(e) => setRifaForm({...rifaForm, precio: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Total de Números</label>
                  <input
                    type="number"
                    value={rifaForm.totalNumeros}
                    onChange={(e) => setRifaForm({...rifaForm, totalNumeros: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    min="1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Fecha del Sorteo</label>
                <input
                  type="date"
                  value={rifaForm.fechaSorteo}
                  onChange={(e) => setRifaForm({...rifaForm, fechaSorteo: e.target.value})}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                />
              </div>
              
            <div className="bg-yellow-900 bg-opacity-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <h4 className="text-yellow-400 font-bold mb-2 text-sm sm:text-base">💳 Datos para Transferencias</h4>
              <p className="text-yellow-200 text-xs sm:text-sm mb-3 sm:mb-4">
                Los compradores necesitarán estos datos para realizar la transferencia
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">Alias</label>
                    <input
                      type="text"
                    value={rifaForm.alias}
                    onChange={(e) => setRifaForm({...rifaForm, alias: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    placeholder="mi.alias"
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">CBU</label>
                    <input
                      type="text"
                    value={rifaForm.cbu}
                    onChange={(e) => setRifaForm({...rifaForm, cbu: e.target.value})}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none text-sm sm:text-base"
                    placeholder="1234567890123456789012"
                    />
                  </div>
              </div>
              <p className="text-yellow-200 text-xs mt-2">
                * Completa al menos uno de los dos campos (Alias o CBU)
              </p>
            </div>
            
            <div className="bg-blue-900 bg-opacity-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
              <h4 className="text-blue-400 font-bold mb-2 text-sm sm:text-base">💬 Contacto WhatsApp (Opcional)</h4>
              <p className="text-blue-200 text-xs sm:text-sm mb-3 sm:mb-4">
                Agrega tu número de WhatsApp para que los compradores puedan contactarte directamente
              </p>
                  <div>
                <label className="block text-white mb-1 sm:mb-2 font-semibold text-sm sm:text-base">WhatsApp</label>
                    <input
                      type="tel"
                  value={rifaForm.whatsapp}
                  onChange={(e) => {
                    // Solo permitir números
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setRifaForm({...rifaForm, whatsapp: value});
                  }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-white bg-opacity-20 text-white border border-blue-400 focus:border-green-400 focus:outline-none text-sm sm:text-base"
                  placeholder="5491112345678"
                  maxLength={15}
                    />
                  </div>
              <p className="text-blue-200 text-xs mt-2">
                * Formato: Código de país + número (ejemplo: 5491112345678) - Solo números
              </p>
            </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
                <button
                  onClick={handleCreateRifa}
                  disabled={isCreatingRifa}
                  className={`flex-1 py-2 sm:py-3 text-white rounded-lg font-bold transition text-sm sm:text-base ${
                    isCreatingRifa 
                      ? 'bg-gray-500 cursor-not-allowed opacity-50' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {isCreatingRifa ? '⏳ Creando...' : 'Crear Rifa'}
                </button>
                <button
                  onClick={() => setShowCreateRifa(false)}
                  disabled={isCreatingRifa}
                  className={`flex-1 py-2 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition text-sm sm:text-base ${
                    isCreatingRifa ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Transferencia */}
      {showTransferenciaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-2xl w-full border-2 border-purple-400">
            <h2 className="text-3xl font-bold text-yellow-400 mb-6 text-center">💳 Instrucciones de Transferencia</h2>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">Datos Bancarios</h3>
              <div className="space-y-3">
                  <div>
                  <p className="text-purple-300 text-sm">CBU</p>
                  <p className="text-white font-bold text-lg">0000003100010123456789</p>
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Alias</p>
                  <p className="text-white font-bold text-lg">ACCESO7.RIFAS</p>
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Titular</p>
                  <p className="text-white font-bold">Juan Pérez</p>
                  </div>
                  <div>
                  <p className="text-purple-300 text-sm">Banco</p>
                  <p className="text-white font-bold">Banco Ejemplo</p>
                  </div>
                  </div>
                </div>

            <div className="bg-yellow-500 bg-opacity-20 rounded-lg p-6 border border-yellow-400 mb-6">
              <h3 className="text-xl font-bold text-yellow-400 mb-4">⚠️ IMPORTANTE</h3>
              <div className="text-center">
                <p className="text-white text-lg mb-4">Incluir en la transferencia el siguiente código:</p>
                <div className="bg-black bg-opacity-40 rounded-lg p-4 mb-4">
                  <p className="text-yellow-400 font-bold text-2xl">REF-{selectedRifa?.id || 'RIFA'}-{new Date().toISOString().split('T')[0].replace(/-/g, '')}-{Math.random().toString(36).substring(2, 6).toUpperCase()}</p>
              </div>
                <p className="text-white text-sm">Este código vincula tu pago con tus números reservados</p>
              </div>
            </div>

            <div className="bg-blue-500 bg-opacity-20 rounded-lg p-6 border border-blue-400 mb-6">
              <h3 className="text-xl font-bold text-blue-400 mb-4">📋 Estado de tu Reserva</h3>
              <div className="space-y-2">
                <p className="text-white">• Tus números están <span className="font-bold text-yellow-400">RESERVADOS</span> por 24 horas</p>
                <p className="text-white">• Tiempo restante: <span className="font-bold text-yellow-400">24 horas</span></p>
                <p className="text-white">• Te avisaremos por email cuando se confirme el pago</p>
                <p className="text-white">• Si no se confirma en 24h, los números se liberarán automáticamente</p>
              </div>
            </div>

            <div className="flex gap-3">
                <button
                onClick={() => {
                  setShowTransferenciaModal(false);
                  handleConfirmPayment();
                }}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition"
                >
                ✅ Confirmar Transferencia
                </button>
                <button
                onClick={() => setShowTransferenciaModal(false)}
                  className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                  Cancelar
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Datos Personales */}
      {showDatosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-800 to-indigo-900 rounded-2xl p-8 max-w-4xl w-full border-2 border-purple-400 my-8">
            <h2 className="text-3xl font-bold text-yellow-400 mb-8 text-center">Tus Datos Personales</h2>
            
            <div className="bg-black bg-opacity-30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-white mb-4">Resumen de tu Compra</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-purple-300 text-sm">Rifa</p>
                  <p className="text-white font-bold text-lg">{selectedRifa.titulo}</p>
                  <p className="text-purple-300 text-sm mt-2">Premio</p>
                  <p className="text-white font-bold">{selectedRifa.premio}</p>
                </div>
                <div>
                  <p className="text-purple-300 text-sm">Números seleccionados</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedNumbers.sort((a, b) => a - b).map(num => (
                      <span key={num} className="bg-yellow-500 text-black px-3 py-1 rounded-lg font-bold">
                        {num}
                      </span>
                    ))}
                  </div>
                  <p className="text-purple-300 text-sm">Total a pagar</p>
                  <p className="text-yellow-400 font-bold text-2xl">${(selectedNumbers.length * parseFloat(selectedRifa.precio)).toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="bg-black bg-opacity-30 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-6">Información de Contacto</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white mb-2 font-semibold">Nombre *</label>
                    <input
                      type="text"
                    value={buyerForm.nombre}
                    onChange={(e) => setBuyerForm({...buyerForm, nombre: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Juan"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Apellido *</label>
                    <input
                      type="text"
                    value={buyerForm.apellido}
                    onChange={(e) => setBuyerForm({...buyerForm, apellido: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Pérez"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Correo Electrónico *</label>
                    <input
                    type="email"
                    value={buyerForm.email}
                    onChange={(e) => setBuyerForm({...buyerForm, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="juan@ejemplo.com"
                    required
                    />
                  </div>
                  <div>
                  <label className="block text-white mb-2 font-semibold">Número de Teléfono *</label>
                    <input
                    type="tel"
                    value={buyerForm.telefono}
                    onChange={(e) => setBuyerForm({...buyerForm, telefono: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="+54 9 11 1234-5678"
                    required
                    />
                  </div>
                  <div className="md:col-span-2">
                  <label className="block text-white mb-2 font-semibold">Dirección *</label>
                    <input
                      type="text"
                    value={buyerForm.direccion}
                    onChange={(e) => setBuyerForm({...buyerForm, direccion: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-white bg-opacity-20 text-white border border-purple-400 focus:border-yellow-400 focus:outline-none"
                    placeholder="Av. Corrientes 1234, CABA, Argentina"
                    required
                    />
                  </div>
                </div>
              <p className="text-purple-300 text-sm mt-4">* Campos obligatorios</p>
              </div>

            <div className="flex gap-3 mt-8">
                <button
                onClick={() => setShowDatosModal(false)}
                className="flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition"
                >
                Cancelar
                </button>
                <button
                onClick={handleProceedToConfirmation}
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                Continuar a Confirmación
                </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Condiciones Legales para Habilitar Rifas */}
      {showCondicionesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-purple-900 to-indigo-900 rounded-2xl p-8 max-w-4xl w-full border-4 border-red-500 my-8">
            <div className="text-center mb-6">
              <h2 className="text-4xl font-bold text-yellow-400 mb-2">⚠️ TÉRMINOS Y CONDICIONES LEGALES</h2>
              <p className="text-white text-lg">Antes de continuar, lea atentamente los siguientes términos</p>
            </div>

            <div className="bg-black bg-opacity-40 rounded-xl p-6 mb-6 max-h-96 overflow-y-auto border border-yellow-500">
              <div className="space-y-6 text-white text-justify">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">1. DESCARGO DE RESPONSABILIDAD</h3>
                  <p className="text-gray-200 leading-relaxed">
                    La plataforma Acceso7 ("Plataforma", "nosotros", "nuestro") proporciona únicamente un servicio técnico de gestión de rifas en línea. NO somos responsables del uso indebido, ilegal o fraudulento que cualquier usuario pueda hacer de esta plataforma. El usuario es el único responsable de:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Garantizar que sus rifas cumplen con todas las leyes y regulaciones locales, estatales y nacionales aplicables.</li>
                    <li>Obtener todas las licencias, permisos y autorizaciones necesarias para llevar a cabo rifas.</li>
                    <li>Verificar que las rifas son legales en su jurisdicción antes de crearlas.</li>
                    <li>Cumplir con todas las obligaciones fiscales y tributarias relacionadas con sus rifas.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">2. RESPONSABILIDAD DEL USUARIO</h3>
                  <p className="text-gray-200 leading-relaxed">
                    El usuario reconoce y acepta que:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Es el único responsable de verificar la legalidad de las rifas en su jurisdicción.</li>
                    <li>Deberá consultar con un abogado si tiene dudas sobre la legalidad de su rifa.</li>
                    <li>Será responsable de todos los daños, pérdidas, reclamos o demandas que surjan del uso de la plataforma.</li>
                    <li>Indemnizará y mantendrá indemne a Acceso7 de cualquier reclamo, demanda, costo o gasto que surja de su uso de la plataforma.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">3. LIMITACIÓN DE RESPONSABILIDAD</h3>
                  <p className="text-gray-200 leading-relaxed">
                    EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, ACCESO7 NO SERÁ RESPONSABLE DE:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Cualquier pérdida, daño o lesión directa, indirecta, incidental, especial o consecuente.</li>
                    <li>Pérdidas de beneficios, datos o oportunidades comerciales.</li>
                    <li>Cualquier actividad ilegal realizada por los usuarios.</li>
                    <li>El incumplimiento de obligaciones fiscales o tributarias por parte del usuario.</li>
                    <li>Problemas legales que puedan surgir del uso de la plataforma.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">4. PROHIBICIONES</h3>
                  <p className="text-gray-200 leading-relaxed">
                    El usuario NO podrá:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Utilizar la plataforma para actividades ilegales o fraudulentas.</li>
                    <li>Crear rifas que violen leyes de juegos de azar, loterías o apuestas.</li>
                    <li>Manipular o interferir con el funcionamiento de la plataforma.</li>
                    <li>Utilizar la plataforma para estafar o engañar a otros usuarios.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-400 mb-3">5. ACEPTACIÓN DE RIESGOS</h3>
                  <p className="text-gray-200 leading-relaxed">
                    Al utilizar esta plataforma, el usuario acepta que:
                  </p>
                  <ul className="list-disc list-inside ml-4 mt-2 space-y-2 text-gray-300">
                    <li>Corre con todos los riesgos asociados al uso de la plataforma.</li>
                    <li>Es responsable de su propio cumplimiento legal.</li>
                    <li>No tendrá derecho a reclamar contra Acceso7 por problemas legales que surjan.</li>
                    <li>Acepta estos términos sin condiciones ni reservas.</li>
                  </ul>
                </div>

                <div className="bg-red-900 bg-opacity-50 border-2 border-red-500 rounded-lg p-4">
                  <p className="text-yellow-300 font-bold text-lg">
                    ⚠️ IMPORTANTE: Si tiene dudas sobre la legalidad de sus rifas, consulte con un abogado antes de continuar. Acceso7 NO proporciona asesoramiento legal y NO se hace responsable de problemas legales.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={aceptoCondiciones}
                  onChange={(e) => setAceptoCondiciones(e.target.checked)}
                  className="mt-1 w-6 h-6 text-yellow-500 rounded focus:ring-2 focus:ring-yellow-400 cursor-pointer"
                />
                <span className="text-white text-lg font-semibold group-hover:text-yellow-400 transition">
                  He leído y acepto los términos y condiciones legales expuestos anteriormente. Entiendo que Acceso7 NO se responsabiliza por el mal uso, uso ilegal o fraudulento de esta plataforma. Acepto todos los riesgos y responsabilidades asociados.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
                <button
                  onClick={() => {
                  setShowCondicionesModal(false);
                  navigateTo('/inicio');
                }}
                className="flex-1 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition text-lg"
              >
                No Acepto - Salir
              </button>
              <button
                onClick={() => {
                  if (aceptoCondiciones) {
                    // Guardar la aceptación en localStorage
                    localStorage.setItem('condiciones_habilitar_rifa', 'true');
                    localStorage.setItem('condiciones_fecha_aceptacion', new Date().toISOString());
                    
                    setShowCondicionesModal(false);
                    // Ya estamos en la página de habilitar-rifa, no necesitamos navegar
                  } else {
                    alert('❌ Debe aceptar los términos y condiciones para continuar');
                  }
                }}
                disabled={!aceptoCondiciones}
                className={`flex-1 py-4 font-bold rounded-lg transition text-lg ${
                  aceptoCondiciones
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                ✅ Acepto y Continuar
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
